import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import moment from 'moment-timezone';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { useGridApiRef } from '@mui/x-data-grid-pro';

import { DataAPIContext, GET_LIST, GET_ONE, CREATE } from '../api';
import { ReloadIcon } from '../components/actionIcons';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';

import NotificationMetricsListToolbar from './NotificationMetricsListToolbar';

const STORAGE_KEY_NOTIFICATION_METRICS_TZ = 'notificationMetrics_timezone';
const STORAGE_KEY_NOTIFICATION_METRICS_TZ_MIGRATION = 'notificationMetrics_timezone_migration_v';
const NOTIFICATION_METRICS_TZ_MIGRATION_VERSION = '1';

/** Gap in days from start → end allowed on the live Notification Metrics view only. */
const LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS = 90;

(function migrateNotificationMetricsTimezoneStorageOnce() {
    if (typeof window === 'undefined') return;
    try {
        if (window.localStorage.getItem(STORAGE_KEY_NOTIFICATION_METRICS_TZ_MIGRATION) === NOTIFICATION_METRICS_TZ_MIGRATION_VERSION) {
            return;
        }
        const legacy = window.localStorage.getItem(STORAGE_KEY_NOTIFICATION_METRICS_TZ);
        if (legacy === 'UTC' || legacy === 'Etc/UTC') {
            window.localStorage.removeItem(STORAGE_KEY_NOTIFICATION_METRICS_TZ);
        }
        window.localStorage.setItem(STORAGE_KEY_NOTIFICATION_METRICS_TZ_MIGRATION, NOTIFICATION_METRICS_TZ_MIGRATION_VERSION);
    } catch {
        /* quota / privacy mode */
    }
})();

function getLocalIanaTimezone() {
    let intlTz = null;
    try { intlTz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ignore */ }
    let guessed = null;
    try { guessed = moment.tz.guess(); } catch { /* ignore */ }
    const candidate = intlTz && moment.tz.zone(intlTz) ? intlTz
        : (guessed && moment.tz.zone(guessed) ? guessed : null);
    if (candidate && candidate !== 'UTC' && candidate !== 'Etc/UTC') return candidate;
    return candidate || 'UTC';
}

function readStoredMetricsTimezoneOrLocalDefault(storageKey) {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored && moment.tz.zone(stored)) return stored;
    } catch { /* quota / privacy mode */ }
    return getLocalIanaTimezone();
}

const useStyles = makeStyles((theme) => ({
    root: {
        minWidth: '1000px',
    },
    notice: {
        margin: theme.spacing(1, 2, 0, 2),
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        margin: theme.spacing(3, 2, 1, 2),
    },
    sectionHeaderTitle: {
        fontSize: '1.05rem !important',
        fontWeight: '600 !important',
        color: '#222',
    },
    sectionHeaderSub: {
        fontSize: '0.8rem !important',
        color: '#888',
    },
    // Responsive multi-column grid for event cards. Without the progress bar
    // each row now needs only [chip 110px + gap + counts 110px + gap + rate
    // 64px = ~310px content + card padding ~32px = ~342px hard floor]. 340px
    // is right at that floor — the longest event-code strings (e.g.
    // "tour_participant.trip_flag_event_abandoned_cart") will wrap to two
    // lines, but that's a deliberate trade for density. Practical column
    // counts:
    //   1280px viewport → 3 columns
    //   1440px viewport → 4 columns
    //   1600px viewport → 4 columns
    //   1920px viewport → 5 columns
    //   2560px viewport → 7 columns
    // Going below 340 will cause stat-strip overflow inside narrow cards.
    // Going above (e.g. 400) prevents event-code wrap at the cost of fewer
    // cards per row.
    eventCardsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
        gap: theme.spacing(2),
        margin: theme.spacing(0, 2),
    },
    eventCard: {
        boxShadow: 'none !important',
        border: 'solid 1px #E5E5E5 !important',
        borderRadius: '12px !important',
        // ``marginBottom`` removed — the parent grid's ``gap`` handles spacing.
        // Cards stretch to match the tallest in each row by default; that's
        // the desired behavior so they line up cleanly.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    eventCardContent: {
        padding: '12px 16px !important',
        '&:last-child': { paddingBottom: '12px !important' },
        // Fill the card so the trailing "Tour breakdown" link can be
        // pushed to the bottom-right via ``marginTop: auto`` and stay
        // aligned with peers across rows of variable-height cards.
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    eventCode: {
        fontFamily: 'ui-monospace, Menlo, monospace !important',
        fontSize: '0.85rem !important',
        fontWeight: '600 !important',
        color: '#333',
        // Long event codes (e.g. *_postbooking24h, *_abandoned_cart) wrap
        // gracefully on segment boundaries instead of overflowing narrow cards.
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    },
    eventLabel: {
        fontSize: '0.78rem !important',
        color: '#888',
        marginBottom: theme.spacing(1),
    },
    // Each medium row is a flex container so the stats sit immediately next
    // to the medium chip, instead of being pushed to the far right edge of
    // the card by a stretchy grid column.
    mediumRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(0.5, 0),
        fontSize: '0.88rem',
        '&:not(:last-child)': {
            borderBottom: '1px solid #F2F2F2',
        },
    },
    mediumLabel: {
        flex: '0 0 110px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    mediumChip: {
        fontSize: '0.7rem !important',
        height: '20px !important',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    // Stat strip = [counts | rate]. The progress bar is currently disabled
    // (see comments in MediumRow / EmailNestedRow) — to bring it back,
    // restore the previous grid template ``minmax(120px, 1fr) 110px 64px``
    // and re-add the <StatBar/> JSX.
    mediumStats: {
        flex: '0 0 auto',
        display: 'grid',
        gridTemplateColumns: '110px 64px',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    mediumStatsFull: {
        // Used by SMS "X sent (no read tracking)" and the Viator nested row.
        // Mirrors ``mediumStats``'s first 110px column so "N sent" right-
        // aligns at the same x as the "X / Y" cell in tracked rows above/
        // below — without this, "N sent" would sit flush-left next to the
        // chip and look visually disconnected from the numeric column. The
        // second column is ``auto`` because "(no read tracking)" is too
        // wide for the 64px rate column; letting it grow naturally
        // preserves the previous single-span overflow behavior (text
        // extends into the card's right padding on narrow cards).
        flex: '0 0 auto',
        display: 'grid',
        gridTemplateColumns: '110px auto',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        color: '#666',
    },
    subRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        // Indent reduced from spacing(4)=32px to spacing(3)=24px so
        // ``padding-left + subRowLabel(86px) = 110px`` matches the chip
        // cell (``mediumLabel: 110px``) in the parent ``mediumRow``. This
        // makes the stats grids in subRows (Non-Viator / Viator) start at
        // the same x as the stats grids in tracked rows (WhatsApp / In-
        // App Chat / SMS), so all "X / Y" and "N sent" right edges line
        // up vertically. 24px is still clearly hierarchical.
        padding: theme.spacing(0.25, 0, 0.25, 3),
        // Match the parent ``mediumRow`` font size (0.88rem) so Viator /
        // Non-Viator splits read at the same weight as their sibling SMS /
        // WhatsApp / In-App Chat rows. Indentation alone communicates the
        // hierarchy — no need for a smaller font on top of that.
        fontSize: '0.88rem',
        color: '#666',
    },
    subRowLabel: {
        flex: '0 0 86px',
        color: '#888',
    },
    subRowStats: {
        flex: '0 0 auto',
        display: 'grid',
        gridTemplateColumns: '110px 64px',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    statBar: {
        position: 'relative',
        height: '6px',
        background: '#EEE',
        borderRadius: '3px',
        overflow: 'hidden',
        width: '100%',
    },
    statBarFill: {
        position: 'absolute',
        left: 0, top: 0, bottom: 0,
        background: '#007AFF',
        borderRadius: '3px',
    },
    statValue: {
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        textAlign: 'right',
    },
    statRate: {
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        textAlign: 'right',
    },
    // Parent row is 0.88rem; shrinking the note keeps "N sent" + note on one
    // line inside narrow event cards without changing card/grid widths.
    noReadTrackingNote: {
        color: '#999',
        fontSize: '0.72rem',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
    },
    kpiContainer: {
        margin: theme.spacing(1, 2, 0, 2),
    },
    kpiCard: {
        height: '100%',
        boxShadow: 'none !important',
        border: 'solid 1px #E5E5E5 !important',
        borderRadius: '12px !important',
    },
    kpiCardContent: {
        padding: '12px 16px !important',
        '&:last-child': { paddingBottom: '12px !important' },
    },
    kpiLabel: {
        fontSize: '0.78rem !important',
        color: '#666',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    kpiValue: {
        fontSize: '1.6rem !important',
        fontWeight: '600 !important',
        marginTop: '4px',
        color: '#111',
    },
    kpiSubvalue: {
        fontSize: '0.8rem !important',
        color: '#888',
        marginTop: '2px',
    },
    daysStrip: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2.5),
        margin: theme.spacing(1.5, 2, 0, 2),
        padding: theme.spacing(1.5, 2),
        border: 'solid 1px #E5E5E5',
        borderRadius: '12px',
        background: '#FAFAFA',
    },
    daysStripStat: {
        display: 'flex',
        flexDirection: 'column',
    },
    daysStripStatLabel: {
        fontSize: '0.72rem !important',
        color: '#777',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    daysStripStatValue: {
        fontSize: '1.05rem !important',
        fontWeight: '600 !important',
        color: '#111',
        fontVariantNumeric: 'tabular-nums',
    },
    byTourGrid: {
        // ``autoHeight`` on the grid handles sizing — no fixed height
        // here. The grid grows to fit the (filtered) row count exactly,
        // collapsing flat against the totals row below it.
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        // Hide the built-in pagination footer entirely. With perPage set
        // to a large value, every row fits on a single "page" and the
        // bottom-of-page "1–N of N" + page navigation arrows are dead
        // weight. The custom ``byTourFooterTotals`` row already shows
        // the count + sums.
        '& .MuiDataGrid-footerContainer': {
            display: 'none',
        },
    },
    byTourFooterTotals: {
        display: 'grid',
        gridTemplateColumns: '3fr 1fr 1fr 1fr 1fr',
        gap: theme.spacing(1),
        margin: theme.spacing(0.5, 2, 0, 2),
        padding: theme.spacing(1, 2),
        border: 'solid 1px #E5E5E5',
        borderRadius: '12px',
        background: '#FAFAFA',
        fontWeight: 600,
        fontSize: '0.85rem',
    },
    stopOffEvent: {
        marginBottom: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        border: 'solid 1px #E5E5E5',
        borderRadius: '8px',
    },
    stopOffEventHeader: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
    },
    stopOffMediums: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    sectionPadding: {
        margin: theme.spacing(0, 2),
    },
    listItem: {},
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    tourBreakdownLink: {
        // ``marginTop: auto`` (in a flex-column parent) pushes this row
        // to the bottom of the card so all the "Tour breakdown" buttons
        // align horizontally across rows of variable-height event cards.
        marginTop: 'auto',
        paddingTop: theme.spacing(1),
        borderTop: '1px solid #F2F2F2',
        display: 'flex',
        justifyContent: 'flex-end',
    },
    tourBreakdownButton: {
        // Pill-shaped text button. Subtle blue tint by default (signals
        // "this is interactive"); brightens on hover for tactile feedback.
        // Picked over a flat text link because the at-a-glance scan
        // across cards reads it as a CTA, not just a footnote.
        fontSize: '0.75rem !important',
        fontWeight: '600 !important',
        textTransform: 'none !important',
        color: '#0A66C2 !important',
        background: 'rgba(10, 102, 194, 0.06) !important',
        borderRadius: '999px !important',
        padding: '3px 8px 3px 10px !important',
        minWidth: '0 !important',
        letterSpacing: '0.01em',
        transition: 'background 120ms ease, color 120ms ease, transform 120ms ease',
        '& .MuiButton-endIcon': {
            marginLeft: '2px',
        },
        '&:hover': {
            background: 'rgba(10, 102, 194, 0.14) !important',
            color: '#0A66C2 !important',
            transform: 'translateX(1px)',
        },
        '&:active': {
            transform: 'translateX(2px)',
        },
        // Disabled state needs to win over the default ``!important``
        // color/background above. MUI applies ``Mui-disabled`` on the
        // button element when ``disabled`` is true; without these
        // overrides the button still looks like an active CTA, which
        // misleads users when the 7-day cap kicks in.
        '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.30) !important',
            background: 'rgba(0, 0, 0, 0.05) !important',
            transform: 'none',
            cursor: 'not-allowed',
        },
        '&:focus-visible': {
            outline: '2px solid rgba(10, 102, 194, 0.4)',
            outlineOffset: '2px',
        },
    },
    drawerPaper: {
        // Narrower than the original 960px so the per-tour cards inside
        // feel proportional to the parent event cards (~340-400px) rather
        // than swimming in whitespace. 100vw clamp keeps it usable on
        // small screens.
        width: 'min(620px, 100vw)',
        maxWidth: '100vw',
    },
    drawerHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: '#FFF',
        borderBottom: '1px solid #E5E5E5',
        padding: theme.spacing(2, 3),
    },
    drawerHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    drawerEventCode: {
        fontFamily: 'ui-monospace, Menlo, monospace !important',
        fontSize: '0.85rem !important',
        color: '#666',
        marginTop: '2px',
        wordBreak: 'break-word',
    },
    drawerSubmeta: {
        fontSize: '0.8rem !important',
        color: '#888',
        marginTop: '4px',
    },
    drawerBody: {
        padding: theme.spacing(2, 3),
    },
    drawerSearchRow: {
        marginBottom: theme.spacing(1.5),
    },
    // === Per-tour cards inside the drawer ============================
    // Each tour is rendered as its own Card, mirroring the parent
    // ``eventCard`` aesthetics (1px #E5E5E5 border, 12px radius, no
    // shadow) so the drawer reads as a list of mini-event-cards rather
    // than a dense table. ``MediumRow`` is reused inside so the medium
    // chips and stat columns are pixel-identical to the source cards.
    tourCardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    tourCard: {
        boxShadow: 'none !important',
        border: 'solid 1px #E5E5E5 !important',
        borderRadius: '12px !important',
        transition: 'border-color 200ms, box-shadow 200ms',
    },
    tourCardExpanded: {
        borderColor: '#1E88E5 !important',
        boxShadow: '0 1px 4px rgba(30,136,229,0.18) !important',
    },
    tourCardContent: {
        padding: '12px 16px !important',
        '&:last-child': { paddingBottom: '12px !important' },
    },
    tourCardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    tourCardHeaderTitle: {
        flex: 1,
        minWidth: 0,
    },
    tourNotifExpandIcon: {
        transition: theme.transitions.create('transform', { duration: 200 }),
    },
    tourNotifPanel: {
        padding: theme.spacing(1.5, 2, 2, 2),
        borderTop: '1px solid #E5E5E5',
        background: '#FAFAFA',
    },
    tourNotifBlock: {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        paddingLeft: theme.spacing(1.5),
        borderTop: '1px solid #ECECEC',
        borderLeft: '3px solid #1E88E5',
        borderRadius: 2,
        '&:first-of-type': {
            marginTop: 0,
            paddingTop: theme.spacing(0.5),
            borderTop: 'none',
        },
    },
    tourNotifTitleRow: {
        marginBottom: theme.spacing(0.75),
        fontSize: '0.82rem',
        fontWeight: 500,
        wordBreak: 'break-word',
    },
    tourCardTitle: {
        // Sans-serif (not monospace like ``eventCode``) since these are
        // human-readable tour names, often wrapped in [code] prefixes.
        // Weight + size match the parent event card title for visual
        // continuity.
        fontSize: '0.95rem !important',
        fontWeight: '600 !important',
        color: '#007AFF',
        textDecoration: 'none',
        wordBreak: 'break-word',
        '&:hover': { textDecoration: 'underline' },
    },
    // Variant for the synthetic "Not attributable to a tour" row. Same
    // size/weight as a real tour title so card heights stay consistent,
    // but italic + gray + non-clickable to communicate "this isn't a
    // navigable trip — it's the orphan bucket that lets totals reconcile."
    tourCardTitleOrphan: {
        color: '#888 !important',
        fontStyle: 'italic',
        cursor: 'default',
        '&:hover': { textDecoration: 'none' },
    },
    // Totals card sits at the bottom of the list with a subtly tinted
    // background so it reads as a summary affordance, not just another
    // row in the stack.
    tourTotalsCard: {
        boxShadow: 'none !important',
        border: 'solid 1px #E5E5E5 !important',
        borderRadius: '12px !important',
        background: '#FAFAFA !important',
        marginTop: theme.spacing(0.5),
        transition: 'border-color 200ms, box-shadow 200ms',
    },
    tourTotalsLabel: {
        fontSize: '0.72rem !important',
        fontWeight: '600 !important',
        color: '#666',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(1),
    },
}));

// Mirror notification_metrics_report_shell.get_event_label()
const EVENT_LABEL_MAP = {
    'tour_participant.created': 'Booking Confirmation',
    'tour_participant.trip_flag_event_24h': '24 Hours Before',
    'tour_participant.trip_flag_event_72h': '72 Hours Before',
    'tour_participant.trip_flag_event_7d': '7 Days Before',
    'tour_participant.trip_flag_event_48h': '48 Hours Before',
    'tour_participant.trip_flag_event_12h': '12 Hours Before',
    'tour_participant.trip_flag_event_2h': '2 Hours Before',
    'tour_participant.trip_flag_event_1h': '1 Hour Before',
    'tour_participant.trip_flag_event_30m': '30 Minutes Before',
    'tour_participant.trip_flag_event_0m': 'Tour Start',
};

function getEventLabel(eventCode) {
    if (!eventCode) return '';
    for (const key of Object.keys(EVENT_LABEL_MAP)) {
        if (eventCode.includes(key)) return EVENT_LABEL_MAP[key];
    }
    return eventCode
        .replace('tour_participant.', '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

const MEDIUM_LABEL = {
    chat: 'In-app Chat',
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    airbnb: 'Airbnb',
};

const MEDIUM_COLOR = {
    chat: '#7C3AED',
    email: '#0EA5E9',
    sms: '#F59E0B',
    whatsapp: '#10B981',
    airbnb: '#EF4444',
};

const MEDIUM_DISPLAY_ORDER = ['email', 'sms', 'whatsapp', 'chat', 'airbnb'];

// Mirrors ``demo.lib.notification_metrics.text_report._EVENT_LABELS`` funnel
// order so Notification Metrics vs CH Test lay out identical timelines; longest
// prefix wins so ``trip_flag_event_24h`` does not match ``..._2h`` first.
const NOTIFICATION_METRICS_EVENT_CODE_PREFIX_ORDER = [
    'tour_participant.created',
    'tour_participant.trip_flag_event_24h',
    'tour_participant.trip_flag_event_72h',
    'tour_participant.trip_flag_event_7d',
    'tour_participant.trip_flag_event_48h',
    'tour_participant.trip_flag_event_12h',
    'tour_participant.trip_flag_event_2h',
    'tour_participant.trip_flag_event_1h',
    'tour_participant.trip_flag_event_30m',
    'tour_participant.trip_flag_event_0m',
];

function notificationMetricsEventCodeSortTier(eventCode) {
    let matched = null;
    let bestLen = -1;
    for (const prefix of NOTIFICATION_METRICS_EVENT_CODE_PREFIX_ORDER) {
        if (!eventCode.includes(prefix)) continue;
        if (prefix.length > bestLen) {
            bestLen = prefix.length;
            matched = prefix;
        }
    }
    if (!matched) {
        return NOTIFICATION_METRICS_EVENT_CODE_PREFIX_ORDER.length;
    }
    return NOTIFICATION_METRICS_EVENT_CODE_PREFIX_ORDER.indexOf(matched);
}

function compareNotificationMetricsEventCodes(a, b) {
    const ta = notificationMetricsEventCodeSortTier(a);
    const tb = notificationMetricsEventCodeSortTier(b);
    if (ta !== tb) return ta - tb;
    return a.localeCompare(b, undefined, { numeric: true });
}

function num(n) {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return Number(n).toLocaleString();
}

function pct(n) {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return `${Number(n).toFixed(2)}%`;
}

// Kept for when the bar is re-enabled (see the mediumStats CSS comment).
// eslint-disable-next-line no-unused-vars
function StatBar({ rate }) {
    const classes = useStyles();
    const v = Math.max(0, Math.min(100, Number(rate) || 0));
    return (
        <div className={classes.statBar}>
            <div className={classes.statBarFill} style={{ width: `${v}%` }} />
        </div>
    );
}

function MediumRow({ medium, metrics, chipOnly = false }) {
    const classes = useStyles();

    // SMS has no read tracking — show the single "X sent" string instead of
    // the bar/counts/rate triplet.
    const noReadTracking = medium === 'sms';

    // WhatsApp uses dedup-collapsed counts to mirror the text report and
    // avoid double-counting templates + their expanded follow-ups.
    //
    // The "full message read" rate (full_msg_read / dedup_read) is only
    // meaningful for the two-step template pattern: a canned *preview*
    // template (e.g. "Thank you for booking …") is sent first, followed by
    // the full message keyed to the same event_log_id. There, dedup_read =
    // "saw the preview or the full message" and full_msg_read = "opened the
    // full body", so the ratio answers "of those who saw anything, what %
    // opened the full message".
    //
    // Notifications sent as a *single* message with no preview template
    // (e.g. tour_participant.survey) have no preview/full split — every read
    // is a full-message read — so full_msg_read == dedup_read by
    // construction and the ratio is a meaningless, constant 100%. Detect
    // that (full_msg_read >= dedup_read) and fall back to the honest
    // unique-send read rate (dedup_read / dedup_total). Raw read/total is
    // the final fallback for very old pre-dedup rollups with no dedup data.
    const isWhatsApp = medium === 'whatsapp';
    const dedupTotal = metrics.dedup_total || 0;
    const dedupRead = metrics.dedup_read || 0;
    const dedupFullMsgRead = metrics.dedup_full_msg_read || 0;
    const hasFullMsgSplit = isWhatsApp && dedupRead > 0 && dedupFullMsgRead < dedupRead;

    let displayRead;
    let displayTotal;
    let displayRate;
    if (hasFullMsgSplit) {
        displayRead = dedupFullMsgRead;
        displayTotal = dedupRead;
        displayRate = (dedupFullMsgRead / dedupRead) * 100;
    } else if (isWhatsApp && dedupTotal > 0) {
        displayRead = dedupRead;
        displayTotal = dedupTotal;
        displayRate = (dedupRead / dedupTotal) * 100;
    } else {
        displayRead = metrics.read || 0;
        displayTotal = metrics.total || 0;
        displayRate = metrics.read_rate;
    }

    return (
        <div className={classes.mediumRow}>
            <span className={classes.mediumLabel}>
                <Chip
                    size="small"
                    label={MEDIUM_LABEL[medium] || medium}
                    className={classes.mediumChip}
                    style={{ backgroundColor: MEDIUM_COLOR[medium] || '#999', color: 'white' }}
                />
            </span>
            {chipOnly ? null : noReadTracking ? (
                <div className={classes.mediumStatsFull}>
                    <span className={classes.statValue}>{num(displayTotal)} sent</span>
                    <span className={classes.noReadTrackingNote}>(no read tracking)</span>
                </div>
            ) : (
                <div className={classes.mediumStats}>
                    {/* <StatBar rate={displayRate} /> -- bar disabled; see mediumStats CSS comment */}
                    <span className={classes.statValue}>{num(displayRead)} / {num(displayTotal)}</span>
                    <span className={classes.statRate}>{pct(displayRate)}</span>
                </div>
            )}
        </div>
    );
}

function EmailNestedRow({ label, total, read, rate, noReadTracking = false, emphasize = false }) {
    const classes = useStyles();
    // ``emphasize`` is used for the Non-Viator row: since Viator emails have
    // no read tracking, Non-Viator is the *real* email read rate. Bump
    // weight + darken color so the eye lands here instead of the
    // visually-equal "0 reads" Viator line below.
    const rateStyle = emphasize ? { fontWeight: 700, color: '#222' } : undefined;
    const labelStyle = emphasize ? { color: '#222', fontWeight: 600 } : undefined;
    return (
        <div className={classes.subRow}>
            <span className={classes.subRowLabel} style={labelStyle}>{label}</span>
            {noReadTracking ? (
                <div className={classes.mediumStatsFull}>
                    <span className={classes.statValue}>{num(total)} sent</span>
                    <span className={classes.noReadTrackingNote}>(no read tracking)</span>
                </div>
            ) : (
                <div className={classes.subRowStats}>
                    {/* <StatBar rate={rate} /> -- bar disabled; see subRowStats CSS comment */}
                    <span className={classes.statValue}>{num(read)} / {num(total)}</span>
                    <span className={classes.statRate} style={rateStyle}>{pct(rate)}</span>
                </div>
            )}
        </div>
    );
}

// Renders one medium's stat row, automatically expanding email-with-
// Viator into the chip-only main row + Non-Viator + Viator sub-rows
// (the same shape ``EventBlock`` shows on the main card). Extracted so
// the per-tour drawer cards and the drawer totals card render
// identically to the main event card.
function MediumRowWithSplit({ medium, metrics }) {
    const hasViatorSplit = medium === 'email' && metrics.viator_total > 0;
    return (
        <>
            {/* When email has a Viator split, the parent EMAIL
                aggregate is misleading — it includes Viator's
                untrackable sends in the denominator with zero
                reads, dragging the rate down. Render chip-only
                in that case and let the Non-Viator split below
                serve as the de facto headline. */}
            <MediumRow medium={medium} metrics={metrics} chipOnly={hasViatorSplit} />
            {hasViatorSplit && (
                <>
                    <EmailNestedRow
                        label="Non-Viator"
                        total={(metrics.total || 0) - (metrics.viator_total || 0)}
                        read={(metrics.read || 0) - (metrics.viator_read || 0)}
                        rate={
                            (metrics.total || 0) - (metrics.viator_total || 0) > 0
                                ? ((metrics.read || 0) - (metrics.viator_read || 0)) /
                                  ((metrics.total || 0) - (metrics.viator_total || 0)) * 100
                                : 0
                        }
                        emphasize
                    />
                    {/* Viator emails are sent through Viator's
                        platform and don't return read tracking,
                        so display the SMS-style "X sent" instead
                        of an artificial 0% read rate. */}
                    <EmailNestedRow
                        label="Viator"
                        total={metrics.viator_total}
                        noReadTracking
                    />
                </>
            )}
        </>
    );
}

// CP-8080: consolidated per-event "read via any method" row. Counts are
// distinct guests — someone who received both an email and an SMS/WhatsApp
// message for this event counts once — so, unlike a sum of the per-medium
// rows above it, the number is safe to compare against sales conversions
// without cross-channel double counting.
function UniqueReachedRow({ stats, loading = false }) {
    const classes = useStyles();
    const hasStats = Boolean(stats && stats.unique_recipients > 0);
    // The distinct-guest counts come from a separate, slower request than the
    // per-medium rows above. While that request is in flight, hold the row's
    // place with a skeleton so it doesn't pop in out of nowhere once the
    // response lands (or silently never appear). Events that turn out to have
    // no unique-reached data drop the placeholder when loading finishes.
    if (!hasStats && !loading) return null;
    if (!hasStats) {
        return (
            <>
                <Divider style={{ margin: '6px 0 4px 0' }} />
                <div className={classes.mediumRow}>
                    <span className={classes.mediumLabel}>
                        <Tooltip
                            title="Computing distinct-guest reach across all channels…"
                            arrow
                            placement="right"
                        >
                            <Chip
                                size="small"
                                label="Any method"
                                className={classes.mediumChip}
                                style={{ backgroundColor: '#334155', color: 'white', opacity: 0.55 }}
                            />
                        </Tooltip>
                    </span>
                    <div className={classes.mediumStats} aria-label="Loading any-method reach">
                        {/* justifySelf mirrors the right-aligned statValue /
                            statRate cells so the skeletons sit exactly where
                            the numbers will land. */}
                        <Skeleton variant="text" width={72} style={{ justifySelf: 'end' }} />
                        <Skeleton variant="text" width={44} style={{ justifySelf: 'end' }} />
                    </div>
                </div>
            </>
        );
    }
    const smsOnly = stats.sms_only_recipients || 0;
    const bothChannels = stats.email_and_text_recipients || 0;
    // Extra paragraphs (overlap / SMS-only) are separate blocks so they don't
    // run on from the definition sentence in the tooltip.
    const tooltip = (
        <div>
            <div>
                Distinct guests reached by this notification in the range. Each guest
                {' '}counts once, even if they were messaged on more than one channel —
                {' '}email, SMS, WhatsApp, in-app chat, or Airbnb. &quot;Read&quot; means
                {' '}they opened or read it on at least one of those channels.
            </div>
            {bothChannels > 0 && (
                <div style={{ marginTop: 8 }}>
                    {num(bothChannels)} guest{bothChannels === 1 ? '' : 's'} received both
                    {' '}an email and an SMS/WhatsApp message.
                </div>
            )}
            {smsOnly > 0 && (
                <div style={{ marginTop: 8 }}>
                    {num(smsOnly)} of these guests {smsOnly === 1 ? 'was' : 'were'} reachable
                    {' '}only by SMS, which has no read tracking.
                </div>
            )}
        </div>
    );
    return (
        <>
            <Divider style={{ margin: '6px 0 4px 0' }} />
            <div className={classes.mediumRow}>
                <span className={classes.mediumLabel}>
                    {/* placement=right grows into the card body so left-column
                        cards don't clip the tooltip at the page edge. */}
                    <Tooltip
                        title={tooltip}
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: { sx: { maxWidth: 260 } },
                        }}
                    >
                        <Chip
                            size="small"
                            label="Any method"
                            className={classes.mediumChip}
                            style={{ backgroundColor: '#334155', color: 'white' }}
                        />
                    </Tooltip>
                </span>
                <div className={classes.mediumStats}>
                    <span className={classes.statValue}>
                        {num(stats.unique_read)} / {num(stats.unique_recipients)}
                    </span>
                    <span className={classes.statRate}>{pct(stats.unique_read_rate)}</span>
                </div>
            </div>
        </>
    );
}

function EventBlock({
    eventCode,
    mediumRows,
    uniqueReached = null,
    uniqueReachedLoading = false,
    onShowTourBreakdown,
    tourBreakdownDisabled = false,
    tourBreakdownDisabledReason = null,
}) {
    const classes = useStyles();
    return (
        <Card className={classes.eventCard} variant="outlined">
            <CardContent className={classes.eventCardContent}>
                <Typography className={classes.eventCode}>{eventCode}</Typography>
                <Typography className={classes.eventLabel}>{getEventLabel(eventCode)}</Typography>
                {mediumRows.map(({ medium, metrics }) => (
                    <MediumRowWithSplit key={medium} medium={medium} metrics={metrics} />
                ))}
                <UniqueReachedRow stats={uniqueReached} loading={uniqueReachedLoading} />
                {onShowTourBreakdown && (
                    <div className={classes.tourBreakdownLink}>
                        <Tooltip
                            title={
                                tourBreakdownDisabled
                                    ? tourBreakdownDisabledReason
                                    : 'Compute tour-by-tour breakdown for this event (~2-5s, cached).'
                            }
                            arrow
                            placement="top"
                        >
                            {/* MUI disables tooltips on disabled buttons unless wrapped — and
                                a disabled button has ``pointer-events: none`` so the
                                ``not-allowed`` cursor has to live on this wrapping span,
                                not on the button itself. */}
                            <span style={tourBreakdownDisabled ? { cursor: 'not-allowed' } : undefined}>
                                <Button
                                    size="small"
                                    disableElevation
                                    disableRipple={false}
                                    className={classes.tourBreakdownButton}
                                    startIcon={<InsightsOutlinedIcon style={{ fontSize: 15 }} />}
                                    endIcon={<NavigateNextIcon style={{ fontSize: 16 }} />}
                                    onClick={() => onShowTourBreakdown(eventCode)}
                                    aria-label={`Tour breakdown for ${eventCode}`}
                                    disabled={tourBreakdownDisabled}
                                >
                                    Tour breakdown
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function formatTourBreakdownEngine(engine) {
    if (engine === 'clickhouse_event') return 'ClickHouse (event table)';
    if (engine === 'postgres_messagelog') return 'Postgres (MessageLog)';
    return engine || '—';
}

const RECIPIENT_MEDIUM_LABELS = {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    chat: 'App Chat',
    airbnb: 'Airbnb',
};

// Same link shape as CampaignAudienceBuilder's ConversationLinkCell. Unique
// names are used raw — do not encodeURIComponent them (CP-8011).
const buildRecipientConversationLink = (u) => {
    const gtcc = u?.conversation_group_tour_chat_channel_id;
    const uniqueName = u?.conversation_unique_name;
    if (!gtcc || !uniqueName) return null;
    return `/chat/${gtcc}/conversations/${uniqueName}/interactivechat`;
};

const recipientReadColor = (read) => (
    read === true ? '#12B76A' : read === false ? '#D92D20' : '#98A2B3'
);

function RecipientStatusDot({ read }) {
    const label = read === true ? 'Read' : read === false ? 'Not read' : 'No read tracking (SMS only)';
    return (
        <Tooltip title={label} arrow>
            <span
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: recipientReadColor(read),
                    display: 'inline-block',
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
}

/**
 * Drawer level 3 (CP-8092): the recipients of one notification row — who
 * read / didn't read, their tags (reseller attribution), and profile/chat
 * links. Backend returns rows unread-first.
 */
function NotificationRecipientsPanel({ slot }) {
    if (!slot) return null;
    if (slot.loading) {
        return (
            <Box display="flex" alignItems="center" gap={1} py={0.5}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                    Loading recipients…
                </Typography>
            </Box>
        );
    }
    if (slot.error) {
        return <Alert severity="error">{slot.error}</Alert>;
    }
    if (slot.engine === 'error') {
        return (
            <Alert severity="warning">
                Could not load recipients. Try refreshing the drawer.
                {slot.engineErrorDetail && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                        {slot.engineErrorDetail}
                    </Typography>
                )}
            </Alert>
        );
    }
    const users = slot.data?.users || [];
    if (users.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No recipients matched this notification for the selected filters.
            </Typography>
        );
    }
    return (
        <div>
            {slot.data?.truncated && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Showing the first {users.length.toLocaleString()} of{' '}
                    {Number(slot.data.total_users).toLocaleString()} recipients (unread first).
                </Typography>
            )}
            {users.map((u) => {
                const chatHref = buildRecipientConversationLink(u);
                return (
                    <div
                        key={u.user_id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 6,
                            padding: '3px 0',
                        }}
                    >
                        <RecipientStatusDot read={u.read} />
                        <Link
                            href={u.user_dashboard_path || `/user/${u.user_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontSize: 13, fontWeight: 500 }}
                            title={u.email || undefined}
                        >
                            {u.full_name || u.email || `User #${u.user_id}`}
                            <OpenInNewIcon sx={{ fontSize: 12, ml: 0.4, verticalAlign: 'middle', opacity: 0.55 }} />
                        </Link>
                        {(u.tags || []).map((tag) => (
                            <Chip
                                key={tag.id}
                                label={tag.name}
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: 11,
                                    bgcolor: '#E4E7EC',
                                    color: '#475467',
                                    borderRadius: 1,
                                }}
                            />
                        ))}
                        <span style={{ flexGrow: 1 }} />
                        {Object.entries(u.mediums || {}).map(([m, cell]) => (
                            <Tooltip
                                key={m}
                                title={cell.read === true ? 'Read' : cell.read === false ? 'Not read' : 'No read tracking'}
                                arrow
                            >
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ color: recipientReadColor(cell.read), whiteSpace: 'nowrap' }}
                                >
                                    {RECIPIENT_MEDIUM_LABELS[m] || m}
                                    {cell.sent > 1 ? ` ×${cell.sent}` : ''}
                                    {cell.read === true ? ' ✓' : cell.read === false ? ' ✗' : ''}
                                </Typography>
                            </Tooltip>
                        ))}
                        {chatHref && (
                            <Link
                                href={chatHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                Chat
                                <OpenInNewIcon sx={{ fontSize: 13 }} />
                            </Link>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function TourBreakdownDrawer({
    open,
    eventCode,
    dateFrom,
    dateTo,
    timezone,
    mediumsFilter,
    onClose,
    tourBreakdownSource,
    chChatTotals,
    showDebugInfo,
}) {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'report/notification_metrics/tour_breakdown';

    // Internal-only diagnostics in the drawer header (the "· cached"
    // hint and the dotted-underline backend timing tooltip). Shown only
    // when ``showDebugInfo`` is true (PG/CH QA tabs), not on the live
    // Notification Metrics view.
    const userIsAppSupport = (() => {
        try {
            return JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.email
                === 'help@curatedplanet.com';
        } catch (_err) {
            return false;
        }
    })();

    const [data, setData] = React.useState(null);
    const [meta, setMeta] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [tourSearch, setTourSearch] = React.useState('');

    // Bumping ``reloadToken`` triggers a refetch with ``refresh=1`` so the
    // backend bypasses its 10-min Redis cache. Used by the header refresh
    // button to grab post-send updates without waiting out the TTL.
    const [reloadToken, setReloadToken] = React.useState(0);

    /** Expand/collapse per-tour "by notification ruleset" breakdown. */
    const [expandedTourNotifKeys, setExpandedTourNotifKeys] = React.useState({});
    /** tour key -> { loading, error, data, engine } */
    const [tourNotifDetailByKey, setTourNotifDetailByKey] = React.useState({});

    const expandedTourNotifRef = React.useRef(expandedTourNotifKeys);
    const tourNotifDetailRef = React.useRef(tourNotifDetailByKey);
    React.useEffect(() => { expandedTourNotifRef.current = expandedTourNotifKeys; }, [expandedTourNotifKeys]);
    React.useEffect(() => { tourNotifDetailRef.current = tourNotifDetailByKey; }, [tourNotifDetailByKey]);

    /** Expand/collapse per-notification recipients (CP-8092, drawer level 3). */
    const [expandedNotifRecipKeys, setExpandedNotifRecipKeys] = React.useState({});
    /** `${tourKey}::${notification_id}` -> { loading, error, data, engine } */
    const [notifRecipDetailByKey, setNotifRecipDetailByKey] = React.useState({});

    const expandedNotifRecipRef = React.useRef(expandedNotifRecipKeys);
    const notifRecipDetailRef = React.useRef(notifRecipDetailByKey);
    React.useEffect(() => { expandedNotifRecipRef.current = expandedNotifRecipKeys; }, [expandedNotifRecipKeys]);
    React.useEffect(() => { notifRecipDetailRef.current = notifRecipDetailByKey; }, [notifRecipDetailByKey]);

    const tourBreakdownRowKey = React.useCallback((tour) => (
        tour.tour_id === '__all__' ? '__all__' : tour.tour_id == null ? 'orphan' : String(tour.tour_id)
    ), []);

    React.useEffect(() => {
        if (!open) {
            setExpandedTourNotifKeys({});
            setTourNotifDetailByKey({});
            setExpandedNotifRecipKeys({});
            setNotifRecipDetailByKey({});
        }
    }, [open]);

    React.useEffect(() => {
        setExpandedTourNotifKeys({});
        setTourNotifDetailByKey({});
        setExpandedNotifRecipKeys({});
        setNotifRecipDetailByKey({});
    }, [eventCode, dateFrom, dateTo, timezone, mediumsFilter, reloadToken, tourBreakdownSource, chChatTotals]);

    const handleToggleTourNotifications = React.useCallback((tour) => {
        const k = tourBreakdownRowKey(tour);
        const wasOpen = !!expandedTourNotifRef.current[k];
        setExpandedTourNotifKeys((p) => ({ ...p, [k]: !wasOpen }));
        if (wasOpen) return;

        const slot = tourNotifDetailRef.current[k];
        if (slot?.loading || (slot?.data != null && !slot?.error)) return;

        const notifResource = 'report/notification_metrics/tour_notification_breakdown';
        setTourNotifDetailByKey((p) => ({
            ...p,
            [k]: { loading: true, error: null, data: null, engine: null },
        }));
        (async () => {
            try {
                const filtersForNotif = [
                    { field: 'event_code', value: eventCode },
                    { field: 'start_datetime', value: dateFrom },
                    { field: 'end_datetime', value: dateTo },
                    { field: 'timezone', value: timezone },
                    { field: 'mediums', value: mediumsFilter },
                    { field: 'tour_id', value: tour.tour_id },
                ];
                const extraParams = {};
                if (reloadToken > 0) extraParams.refresh = '1';
                if (tourBreakdownSource) extraParams.tour_breakdown_source = tourBreakdownSource;
                if (chChatTotals) extraParams.ch_chat_totals = chChatTotals;
                const res = await dataAPI(GET_ONE, notifResource, {
                    filter: { filters: JSON.stringify(filtersForNotif) },
                    ...extraParams,
                });
                if (res?.statusCode >= 400) {
                    throw new Error(`Failed to load (HTTP ${res.statusCode})`);
                }
                setTourNotifDetailByKey((p) => ({
                    ...p,
                    [k]: {
                        loading: false,
                        data: res?.data?.data?.notifications ?? [],
                        engine: res?.data?.meta?.tour_notification_breakdown_engine,
                        engineErrorDetail: res?.data?.data?.engine_error_detail || null,
                        error: null,
                    },
                }));
            } catch (err) {
                setTourNotifDetailByKey((p) => ({
                    ...p,
                    [k]: {
                        loading: false,
                        data: null,
                        engine: null,
                        error: err?.message || 'Failed to load notification breakdown',
                    },
                }));
            }
        })();
    }, [
        tourBreakdownRowKey,
        eventCode,
        dateFrom,
        dateTo,
        timezone,
        mediumsFilter,
        dataAPI,
        tourBreakdownSource,
        chChatTotals,
        reloadToken,
    ]);

    const handleToggleNotifRecipients = React.useCallback((tourIdValue, nid) => {
        const tourKey = tourBreakdownRowKey({ tour_id: tourIdValue });
        const k = `${tourKey}::${nid}`;
        const wasOpen = !!expandedNotifRecipRef.current[k];
        setExpandedNotifRecipKeys((p) => ({ ...p, [k]: !wasOpen }));
        if (wasOpen) return;

        const slot = notifRecipDetailRef.current[k];
        if (slot?.loading || (slot?.data != null && !slot?.error)) return;

        const recipResource = 'report/notification_metrics/notification_recipients';
        setNotifRecipDetailByKey((p) => ({
            ...p,
            [k]: { loading: true, error: null, data: null, engine: null },
        }));
        (async () => {
            try {
                const filtersForRecipients = [
                    { field: 'event_code', value: eventCode },
                    { field: 'start_datetime', value: dateFrom },
                    { field: 'end_datetime', value: dateTo },
                    { field: 'timezone', value: timezone },
                    { field: 'mediums', value: mediumsFilter },
                    { field: 'tour_id', value: tourIdValue },
                    { field: 'notification_id', value: nid },
                ];
                const extraParams = {};
                if (reloadToken > 0) extraParams.refresh = '1';
                if (tourBreakdownSource) extraParams.tour_breakdown_source = tourBreakdownSource;
                if (chChatTotals) extraParams.ch_chat_totals = chChatTotals;
                const res = await dataAPI(GET_ONE, recipResource, {
                    filter: { filters: JSON.stringify(filtersForRecipients) },
                    ...extraParams,
                });
                if (res?.statusCode >= 400) {
                    throw new Error(`Failed to load (HTTP ${res.statusCode})`);
                }
                setNotifRecipDetailByKey((p) => ({
                    ...p,
                    [k]: {
                        loading: false,
                        data: res?.data?.data || null,
                        engine: res?.data?.meta?.notification_recipients_engine,
                        engineErrorDetail: res?.data?.data?.engine_error_detail || null,
                        error: null,
                    },
                }));
            } catch (err) {
                setNotifRecipDetailByKey((p) => ({
                    ...p,
                    [k]: {
                        loading: false,
                        data: null,
                        engine: null,
                        error: err?.message || 'Failed to load recipients',
                    },
                }));
            }
        })();
    }, [
        tourBreakdownRowKey,
        eventCode,
        dateFrom,
        dateTo,
        timezone,
        mediumsFilter,
        dataAPI,
        tourBreakdownSource,
        chChatTotals,
        reloadToken,
    ]);

    // ``rollingUp`` is true while the refresh button is force-rolling
    // today's bucket on the server (before the cache-bypass refetch
    // fires). We surface a distinct "Updating today's metrics…" line in
    // the body so users understand the extra latency vs a plain refetch.
    const [rollingUp, setRollingUp] = React.useState(false);

    const handleRefreshClick = React.useCallback(async () => {
        // Two-step refresh:
        //   1) POST /rollup_today/ with the drawer's date range. The
        //      backend rolls up only today's UTC bucket if today falls
        //      in range (cheap: ~60ms-1.5s). This advances
        //      NotificationRollupCheckpoint.computed_at, which in turn
        //      advances the ``rollup_horizon`` clip the drawer's GET
        //      uses, so any messages sent in the last 15 minutes
        //      become visible.
        //   2) Bump reloadToken to refetch with ?refresh=1 (bypasses
        //      the 10-min Redis cache).
        //
        // We don't block the refetch on rollup failure — the user
        // still gets *something* fresher than what they had, and the
        // existing 15-min cron will catch up regardless.
        setRollingUp(true);
        try {
            await dataAPI(CREATE, 'report/notification_metrics/rollup_today', {
                data: { date_from: dateFrom, date_to: dateTo, timezone },
            });
        } catch (_err) {
            // Soft-fail: still proceed with refetch.
        } finally {
            setRollingUp(false);
            setReloadToken((n) => n + 1);
        }
    }, [dataAPI, dateFrom, dateTo, timezone]);

    React.useEffect(() => {
        if (!open || !eventCode) return undefined;
        const ac = new AbortController();
        let active = true;
        (async () => {
            setLoading(true);
            setError(null);
            // Drop previous breakdown immediately when the window / filters /
            // event change so we never show stale tour rows beside a loading
            // state (Redis cache TTL is separate — this fixes client cache).
            setData(null);
            setMeta(null);
            try {
                const filtersForBreakdown = [
                    { field: 'event_code', value: eventCode },
                    { field: 'start_datetime', value: dateFrom },
                    { field: 'end_datetime', value: dateTo },
                    { field: 'timezone', value: timezone },
                    { field: 'mediums', value: mediumsFilter },
                ];
                const isRefresh = reloadToken > 0;
                const extraParams = {};
                if (isRefresh) extraParams.refresh = '1';
                if (tourBreakdownSource) extraParams.tour_breakdown_source = tourBreakdownSource;
                if (chChatTotals) extraParams.ch_chat_totals = chChatTotals;
                const res = await dataAPI(GET_ONE, resource, {
                    filter: { filters: JSON.stringify(filtersForBreakdown) },
                    ...extraParams,
                    signal: ac.signal,
                });
                if (!active) return;
                if (res?.statusCode >= 400) {
                    setError(`Failed to load (HTTP ${res.statusCode})`);
                    setData(null);
                } else {
                    setData(res?.data?.data || null);
                    setMeta(res?.data?.meta || null);
                }
            } catch (err) {
                if (active && err?.name !== 'AbortError') {
                    setError(err?.message || 'Failed to load tour breakdown');
                    setData(null);
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; ac.abort(); };
    }, [open, eventCode, dateFrom, dateTo, timezone, mediumsFilter, reloadToken, dataAPI, resource, tourBreakdownSource, chChatTotals]);

    // Reset transient drawer state when the event changes (or the drawer
    // closes), so opening a new event doesn't briefly flash stale data.
    React.useEffect(() => {
        if (!open) {
            setData(null);
            setMeta(null);
            setError(null);
            setTourSearch('');
            setReloadToken(0);
        }
    }, [open]);

    const filteredTours = React.useMemo(() => {
        const tours = data?.tours || [];
        const q = tourSearch.trim().toLowerCase();
        if (!q) return tours;
        return tours.filter((t) => (t.tour_name || '').toLowerCase().includes(q));
    }, [data, tourSearch]);

    // The orphan ("not attributable to a tour") row carries ``tour_id =
    // null`` and is pinned to the bottom of ``data.tours`` by the backend.
    // It exists purely so per-medium totals reconcile to the main event
    // card; it is not a tour. Strip it out of every visible "X tours"
    // count so the labels stay honest.
    const realTourCount = React.useMemo(
        () => (data?.tours || []).filter((t) => t.tour_id != null).length,
        [data],
    );
    const realFilteredTourCount = React.useMemo(
        () => filteredTours.filter((t) => t.tour_id != null).length,
        [filteredTours],
    );

    // The visible totals row recomputes from the filtered subset so the
    // bottom strip reflects what's on screen, matching the App Installs by
    // Tour pattern. Without this, "TOTAL" would lie about the visible sum
    // when a search is active.
    // Per-medium totals across the visible tour set. We deliberately do
    // NOT show a cross-medium "TOTAL sends / read / read rate" — guests
    // typically engage on one channel, so summing email + sms + WhatsApp
    // either double-counts intent or hides which channel actually worked.
    // Per-medium totals make the medium-by-medium reach and engagement
    // legible without that conflation.
    const visibleTotals = React.useMemo(() => {
        if (!data) return null;
        const q = tourSearch.trim();
        if (!q) return { tours: realTourCount, by_medium: data.totals.by_medium };
        const summed = filteredTours.reduce(
            (acc, t) => {
                if (t.tour_id != null) acc.tours += 1;
                for (const [m, stats] of Object.entries(t.mediums || {})) {
                    if (!stats) continue;
                    if (!acc.by_medium[m]) acc.by_medium[m] = { sends: 0, read: 0 };
                    acc.by_medium[m].sends += stats.total || 0;
                    if (stats.read != null) acc.by_medium[m].read += stats.read;
                }
                return acc;
            },
            { tours: 0, by_medium: {} },
        );
        return summed;
    }, [data, tourSearch, filteredTours, realTourCount]);

    const mediumKeys = mediumsFilter && mediumsFilter.length > 0
        ? mediumsFilter
        : MEDIUM_DISPLAY_ORDER;

    const tourBreakdownReadSource = data?.tour_breakdown_read_source ?? meta?.tour_breakdown_read_source;
    const tourNotifExpandEnabled = tourBreakdownReadSource === 'clickhouse_event'
        || tourBreakdownReadSource === 'postgres_messagelog';

    // Build the metrics object expected by ``MediumRow`` from a per-tour
    // (medium → stats) entry. Returns ``null`` when the medium had no
    // activity for this tour so callers can drop the row entirely (no
    // grayed-out placeholder noise).
    //
    // viator_* (email) + dedup_* (whatsapp) are passed through so the
    // per-tour render can use the same email-with-Viator-split + WA-
    // dedup-collapsed display the main event card uses (see
    // ``EventBlock`` and the ``MediumRow`` dedup branch). Without
    // these, drawer numbers wouldn't reconcile with the card (raw WA
    // ~80%+ vs card's dedup-collapsed ~58%).
    const tourMetricsForMediumRow = (stats) => {
        if (!stats || (stats.total || 0) <= 0) return null;
        return {
            total: stats.total,
            read: stats.read,
            read_rate: stats.read_rate,
            viator_total: stats.viator_total,
            viator_read: stats.viator_read,
            viator_read_rate: stats.viator_read_rate,
            dedup_total: stats.dedup_total,
            dedup_read: stats.dedup_read,
            dedup_full_msg_read: stats.dedup_full_msg_read,
        };
    };

    // Build a ``MediumRow``-compatible metrics object from the visible
    // totals' ``by_medium`` summary. We pass through the per-medium
    // extras (viator_*, dedup_*) the backend aggregates across tours
    // so the totals card matches the per-tour rows above it. SMS sails
    // through the ``noReadTracking`` branch in MediumRow so the sends
    // count is enough; for everything else we recompute the read_rate
    // to match the visible (filtered) tour set.
    const totalsMetricsForMediumRow = (totals) => {
        const sends = totals?.sends || 0;
        if (sends <= 0) return null;
        const read = totals?.read || 0;
        const rate = sends > 0 ? Math.round((read / sends) * 10000) / 100 : 0;
        const out = { total: sends, read, read_rate: rate };
        if (totals.viator_total != null) {
            const vt = totals.viator_total || 0;
            const vr = totals.viator_read || 0;
            out.viator_total = vt;
            out.viator_read = vr;
            out.viator_read_rate = vt > 0 ? Math.round((vr / vt) * 10000) / 100 : 0;
        }
        if (totals.dedup_total != null) {
            out.dedup_total = totals.dedup_total;
            out.dedup_read = totals.dedup_read;
            out.dedup_full_msg_read = totals.dedup_full_msg_read;
        }
        return out;
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ className: classes.drawerPaper }}
        >
            <div className={classes.drawerHeader}>
                <div className={classes.drawerHeaderRow}>
                    <Typography style={{ flex: 1, fontSize: '1.1rem', fontWeight: 600 }}>
                        Tour Breakdown — {getEventLabel(eventCode || '')}
                    </Typography>
                    <Tooltip
                        title="Refresh: forces a rollup of today's bucket (if today is in range), then re-fetches the breakdown bypassing the cache."
                        arrow
                    >
                        <span>
                            <IconButton
                                size="small"
                                disabled={loading || rollingUp}
                                onClick={handleRefreshClick}
                                aria-label="Refresh"
                            >
                                <ReloadIcon style={{ fontSize: 20 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <CloseIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </div>
                <Typography className={classes.drawerEventCode}>{eventCode}</Typography>
                <Typography className={classes.drawerSubmeta}>
                    {dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : ''}
                    {data?.active_tours_in_range != null && (
                        <> · {realTourCount} tours with activity (of {data.active_tours_in_range} active in range)</>
                    )}
                    {/* Internal-only: see ``userIsAppSupport`` definition above. */}
                    {showDebugInfo && (meta?.cache_hit ? ' · cached' : meta?.execution_time_ms != null && (
                        <>
                            {' · '}
                            {meta.timings_ms ? (
                                // Hover the time number to see where the
                                // backend spent it (id resolve, calculate,
                                // serialize, active-tours, rollup-horizon).
                                // Useful for narrowing the next perf win
                                // without grepping logs.
                                <Tooltip
                                    arrow
                                    title={
                                        <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.75rem' }}>
                                            <div><b>Backend timings (ms):</b></div>
                                            <div>total:          {Math.round(meta.timings_ms.total)}</div>
                                            <div>rollup_horizon: {Math.round(meta.timings_ms.rollup_horizon)}</div>
                                            {meta.timings_ms.clickhouse != null && (
                                                <div>clickhouse:   {Math.round(meta.timings_ms.clickhouse)}</div>
                                            )}
                                            {meta.timings_ms.id_resolve != null && (
                                                <div>id_resolve:     {Math.round(meta.timings_ms.id_resolve)}</div>
                                            )}
                                            {meta.timings_ms.id_resolve_per_medium && Object.entries(meta.timings_ms.id_resolve_per_medium).map(([m, v]) => (
                                                <div key={m}>{`  ${m.padEnd(13)} ${Math.round(v)}`}</div>
                                            ))}
                                            <div>calculate:      {Math.round(meta.timings_ms.calculate)}</div>
                                            <div>serialize:      {Math.round(meta.timings_ms.serialize)}</div>
                                            <div>active_tours:   {Math.round(meta.timings_ms.active_tours)}</div>
                                        </div>
                                    }
                                >
                                    <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                                        {Math.round(meta.execution_time_ms)}ms
                                    </span>
                                </Tooltip>
                            ) : (
                                `${Math.round(meta.execution_time_ms)}ms`
                            )}
                        </>
                    ))}
                </Typography>
                {showDebugInfo && meta && (
                    <Alert severity="info" sx={{ mt: 1.5, mr: 2 }}>
                        <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Tour breakdown backend (testing)
                        </Typography>
                        <Typography variant="body2" component="div">
                            Drawer engine:&nbsp;
                            <strong>{formatTourBreakdownEngine(meta.tour_breakdown_read_source)}</strong>
                        </Typography>
                        {meta.tour_breakdown_read_source === 'clickhouse_event'
                            && meta.tour_breakdown_ch_chat_totals && (
                            <Typography variant="body2" component="div">
                                CH in-app chat totals:&nbsp;
                                <strong>{meta.tour_breakdown_ch_chat_totals}</strong>
                                {' '}
                                <Typography variant="caption" component="span" color="text.secondary">
                                    (<code>pg_parity</code> matches rollup;{' '}
                                    <code>all_sends</code> counts every CH chat send).
                                </Typography>
                            </Typography>
                        )}
                        {tourBreakdownSource && (
                            <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                                Request override:&nbsp;<code>tour_breakdown_source={tourBreakdownSource}</code>
                            </Typography>
                        )}
                        {meta?.timings_ms?.clickhouse != null && userIsAppSupport && (
                            <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                                CH query server time:&nbsp;
                                {Math.round(meta.timings_ms.clickhouse)}ms
                                {' '}(see dotted timing in header for full breakdown)
                            </Typography>
                        )}
                    </Alert>
                )}
                {data?.rollup_horizon_clipped && (
                    // Drawer reads MessageLog live but clips ``end_dt`` to
                    // the latest NotificationRollupCheckpoint so it can
                    // never show a send the main event card hasn't ingested
                    // yet (the card reads from the rolled-up
                    // NotificationMetricsDaily aggregate). Without this
                    // hint, users get confused when refreshing the drawer
                    // doesn't pick up a send they know was just delivered.
                    <Tooltip
                        title="The drawer mirrors the main event card's data freshness so the two always agree. Sends that arrived after the most recent rollup tick aren't shown here yet — they'll appear after the next rollup cycle (about every 30 minutes for today, every 3 hours for trailing days)."
                        arrow
                        placement="bottom-start"
                    >
                        <Typography className={classes.drawerSubmeta} style={{ marginTop: 2 }}>
                            Showing data through {new Date(data.rollup_horizon).toLocaleString()}
                        </Typography>
                    </Tooltip>
                )}
            </div>

            <div className={classes.drawerBody}>
                {(loading || rollingUp) && (
                    <Box>
                        <LinearProgress />
                        <Typography style={{ marginTop: 8, color: '#888', fontSize: '0.85rem', fontStyle: 'italic' }}>
                            {rollingUp
                                ? "Updating today's metrics before refreshing…"
                                : 'Computing tour breakdown for this event…'}
                        </Typography>
                        {!rollingUp && (
                            // The breakdown is computed live against
                            // ``MessageLog`` and isn't pre-aggregated like
                            // the main event cards. Cold-cache loads on
                            // busy operators have measured at 30s+ even
                            // for 7-day ranges, so set expectations
                            // up front rather than letting the user
                            // wonder if the page is hung.
                            <Typography style={{ marginTop: 4, color: '#aaa', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                This may take a few seconds — the first load for a given event &amp; range
                                runs the calculation live; subsequent loads in the same date window are cached and faster.
                            </Typography>
                        )}
                    </Box>
                )}

                {!loading && !rollingUp && error && (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={handleRefreshClick}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {!loading && !rollingUp && !error && data && (data.tours?.length || 0) === 0 && (
                    <Typography style={{ color: '#888', fontStyle: 'italic' }}>
                        No tours had activity for this event in this period.
                    </Typography>
                )}

                {!loading && !rollingUp && !error && data && (data.tours?.length || 0) > 0 && (
                    <>
                        <div className={classes.drawerSearchRow}>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search tours..."
                                value={tourSearch}
                                onChange={(e) => setTourSearch(e.target.value)}
                                sx={{ width: 280 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 18, color: '#888' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: tourSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setTourSearch('')}
                                                aria-label="Clear tour search"
                                            >
                                                <ClearIcon style={{ fontSize: 16 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Typography component="span" sx={{ ml: 2, color: '#888', fontSize: '0.85rem' }}>
                                {tourSearch
                                    ? `${realFilteredTourCount} of ${realTourCount} tours`
                                    : `${realTourCount} tours`}
                            </Typography>
                        </div>

                        <div className={classes.tourCardList}>
                            {filteredTours.map((t) => {
                                const usedMediums = mediumKeys
                                    .map((m) => [m, tourMetricsForMediumRow(t.mediums?.[m])])
                                    .filter(([, metrics]) => metrics);
                                if (usedMediums.length === 0) return null;
                                // Backend pins ``tour_id=null`` (the
                                // "Not attributable to a tour" bucket)
                                // to the bottom of ``data.tours`` so the
                                // drawer's per-medium totals always
                                // reconcile with the main event card. The
                                // row is rendered without a trip link.
                                const isOrphan = t.tour_id == null;
                                const rowKey = tourBreakdownRowKey(t);
                                const notifExpanded = !!expandedTourNotifKeys[rowKey];
                                const notifSlot = tourNotifDetailByKey[rowKey];
                                return (
                                    <Card key={t.tour_id ?? '__orphan__'} className={`${classes.tourCard}${notifExpanded ? ` ${classes.tourCardExpanded}` : ''}`} variant="outlined">
                                        <CardContent className={classes.tourCardContent}>
                                            <div className={classes.tourCardHeader}>
                                                <div className={classes.tourCardHeaderTitle}>
                                                    {isOrphan ? (
                                                        <Tooltip
                                                            title="These messages have no MessageLog → Twilio conversation → Group_Tour link, so they couldn't be attributed to a specific tour. Counted in the totals to keep parity with the main event card."
                                                            arrow
                                                            placement="top-start"
                                                        >
                                                            <span
                                                                className={`${classes.tourCardTitle} ${classes.tourCardTitleOrphan}`}
                                                                title={undefined}
                                                            >
                                                                {t.tour_name}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        <Link
                                                            href={`/trip/${t.tour_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classes.tourCardTitle}
                                                            title={t.tour_name}
                                                        >
                                                            {t.tour_name}
                                                            <OpenInNewIcon
                                                                style={{ fontSize: 14, marginLeft: 6, verticalAlign: 'middle', opacity: 0.55 }}
                                                            />
                                                        </Link>
                                                    )}
                                                </div>
                                                <Tooltip
                                                    title={
                                                        tourNotifExpandEnabled
                                                            ? (notifExpanded ? 'Hide per-notification ruleset breakdown' : 'Show sends and reads by notification ruleset')
                                                            : 'Per-notification breakdown is available when the tour breakdown uses Postgres or ClickHouse.'
                                                    }
                                                    arrow
                                                >
                                                    <span style={tourNotifExpandEnabled ? undefined : { cursor: 'not-allowed' }}>
                                                        <IconButton
                                                            size="small"
                                                            aria-expanded={notifExpanded}
                                                            aria-label="Notification rulesets for this tour"
                                                            disabled={!tourNotifExpandEnabled}
                                                            onClick={() => handleToggleTourNotifications(t)}
                                                        >
                                                            <ExpandMoreIcon
                                                                className={classes.tourNotifExpandIcon}
                                                                style={{
                                                                    transform: notifExpanded ? 'rotate(180deg)' : 'none',
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                            {usedMediums.map(([m, metrics]) => (
                                                <MediumRowWithSplit key={m} medium={m} metrics={metrics} />
                                            ))}
                                        </CardContent>
                                        <Collapse in={notifExpanded} timeout="auto" unmountOnExit>
                                            <div className={classes.tourNotifPanel}>
                                                {notifSlot?.loading && (
                                                    <Box display="flex" alignItems="center" gap={1} py={0.5}>
                                                        <CircularProgress size={18} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Loading notification breakdown…
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {!notifSlot?.loading && notifSlot?.error && (
                                                    <Alert severity="error">{notifSlot.error}</Alert>
                                                )}
                                                {!notifSlot?.loading && !notifSlot?.error && notifSlot?.engine === 'error' && (
                                                    <Alert severity="warning">
                                                        Could not load per-notification metrics. Try refreshing the drawer.
                                                        {notifSlot?.engineErrorDetail && (
                                                            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                                                                {notifSlot.engineErrorDetail}
                                                            </Typography>
                                                        )}
                                                    </Alert>
                                                )}
                                                {!notifSlot?.loading && !notifSlot?.error
                                                    && (notifSlot?.engine === 'clickhouse_event'
                                                        || notifSlot?.engine === 'postgres_messagelog')
                                                    && Array.isArray(notifSlot.data) && notifSlot.data.length === 0 && (
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                        No notification-level sends matched this tour for the selected filters.
                                                    </Typography>
                                                )}
                                                {!notifSlot?.loading && !notifSlot?.error
                                                    && (notifSlot?.engine === 'clickhouse_event'
                                                        || notifSlot?.engine === 'postgres_messagelog')
                                                    && Array.isArray(notifSlot.data) && notifSlot.data.length > 0
                                                    && notifSlot.data.map((row) => {
                                                        const nid = row.notification_id;
                                                        const usedN = mediumKeys
                                                            .map((m) => [m, tourMetricsForMediumRow(row.mediums?.[m])])
                                                            .filter(([, metrics]) => metrics);
                                                        if (usedN.length === 0) return null;
                                                        const title = row.notification_name || `Notification ${nid}`;
                                                        const path = row.notification_dashboard_path;
                                                        const recipKey = `${rowKey}::${nid}`;
                                                        const recipExpanded = !!expandedNotifRecipKeys[recipKey];
                                                        const recipSlot = notifRecipDetailByKey[recipKey];
                                                        return (
                                                            <div key={`${rowKey}-nf-${nid}`} className={classes.tourNotifBlock}>
                                                                <div className={classes.tourNotifTitleRow} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                                                    {path ? (
                                                                        <Link
                                                                            href={path}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className={classes.tourCardTitle}
                                                                            title={title}
                                                                        >
                                                                            {title}
                                                                            <OpenInNewIcon
                                                                                style={{ fontSize: 14, marginLeft: 6, verticalAlign: 'middle', opacity: 0.55 }}
                                                                            />
                                                                        </Link>
                                                                    ) : (
                                                                        <Typography component="span" color="text.secondary" fontStyle="italic">
                                                                            {title}
                                                                        </Typography>
                                                                    )}
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => handleToggleNotifRecipients(t.tour_id, nid)}
                                                                        sx={{ ml: 'auto', minWidth: 0, fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                                                                        aria-expanded={recipExpanded}
                                                                        endIcon={(
                                                                            <ExpandMoreIcon
                                                                                className={classes.tourNotifExpandIcon}
                                                                                sx={{ fontSize: 16 }}
                                                                                style={{ transform: recipExpanded ? 'rotate(180deg)' : 'none' }}
                                                                            />
                                                                        )}
                                                                    >
                                                                        Recipients
                                                                    </Button>
                                                                </div>
                                                                {usedN.map(([m, metrics]) => (
                                                                    <MediumRowWithSplit key={m} medium={m} metrics={metrics} />
                                                                ))}
                                                                <Collapse in={recipExpanded} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #E0E0E0' }}>
                                                                        <NotificationRecipientsPanel slot={recipSlot} />
                                                                    </Box>
                                                                </Collapse>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </Collapse>
                                    </Card>
                                );
                            })}

                            {visibleTotals && (() => {
                                const usedTotals = mediumKeys
                                    .map((m) => [m, totalsMetricsForMediumRow(visibleTotals.by_medium?.[m])])
                                    .filter(([, metrics]) => metrics);
                                if (usedTotals.length === 0) return null;
                                const totalsNotifExpanded = !!expandedTourNotifKeys['__all__'];
                                const totalsNotifSlot = tourNotifDetailByKey['__all__'];
                                return (
                                    <Card className={`${classes.tourTotalsCard}${totalsNotifExpanded ? ` ${classes.tourCardExpanded}` : ''}`} variant="outlined">
                                        <CardContent className={classes.tourCardContent}>
                                            <div className={classes.tourCardHeader}>
                                                <div className={classes.tourCardHeaderTitle}>
                                                    <Typography className={classes.tourTotalsLabel}>
                                                        {tourSearch
                                                            ? `Matching (${realFilteredTourCount} tours)`
                                                            : `Total (${realTourCount} tours)`}
                                                    </Typography>
                                                </div>
                                                <Tooltip
                                                    title={
                                                        tourNotifExpandEnabled
                                                            ? (totalsNotifExpanded ? 'Hide per-notification ruleset breakdown' : 'Show sends and reads by notification ruleset (all tours)')
                                                            : 'Per-notification breakdown is available when the tour breakdown uses Postgres or ClickHouse.'
                                                    }
                                                    arrow
                                                >
                                                    <span style={tourNotifExpandEnabled ? undefined : { cursor: 'not-allowed' }}>
                                                        <IconButton
                                                            size="small"
                                                            aria-expanded={totalsNotifExpanded}
                                                            aria-label="Notification rulesets across all tours"
                                                            disabled={!tourNotifExpandEnabled}
                                                            onClick={() => handleToggleTourNotifications({ tour_id: '__all__' })}
                                                        >
                                                            <ExpandMoreIcon
                                                                className={classes.tourNotifExpandIcon}
                                                                style={{
                                                                    transform: totalsNotifExpanded ? 'rotate(180deg)' : 'none',
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                            {usedTotals.map(([m, metrics]) => (
                                                <MediumRowWithSplit key={m} medium={m} metrics={metrics} />
                                            ))}
                                        </CardContent>
                                        <Collapse in={totalsNotifExpanded} timeout="auto" unmountOnExit>
                                            <div className={classes.tourNotifPanel}>
                                                {totalsNotifSlot?.loading && (
                                                    <Box display="flex" alignItems="center" gap={1} py={0.5}>
                                                        <CircularProgress size={18} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Loading notification breakdown…
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {!totalsNotifSlot?.loading && totalsNotifSlot?.error && (
                                                    <Alert severity="error">{totalsNotifSlot.error}</Alert>
                                                )}
                                                {!totalsNotifSlot?.loading && !totalsNotifSlot?.error && totalsNotifSlot?.engine === 'error' && (
                                                    <Alert severity="warning">
                                                        Could not load per-notification metrics. Try refreshing the drawer.
                                                        {totalsNotifSlot?.engineErrorDetail && (
                                                            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                                                                {totalsNotifSlot.engineErrorDetail}
                                                            </Typography>
                                                        )}
                                                    </Alert>
                                                )}
                                                {!totalsNotifSlot?.loading && !totalsNotifSlot?.error
                                                    && (totalsNotifSlot?.engine === 'clickhouse_event'
                                                        || totalsNotifSlot?.engine === 'postgres_messagelog')
                                                    && Array.isArray(totalsNotifSlot.data) && totalsNotifSlot.data.length === 0 && (
                                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                        No notification-level sends matched for the selected filters.
                                                    </Typography>
                                                )}
                                                {!totalsNotifSlot?.loading && !totalsNotifSlot?.error
                                                    && (totalsNotifSlot?.engine === 'clickhouse_event'
                                                        || totalsNotifSlot?.engine === 'postgres_messagelog')
                                                    && Array.isArray(totalsNotifSlot.data) && totalsNotifSlot.data.length > 0
                                                    && totalsNotifSlot.data.map((row) => {
                                                        const nid = row.notification_id;
                                                        const usedN = mediumKeys
                                                            .map((m) => [m, tourMetricsForMediumRow(row.mediums?.[m])])
                                                            .filter(([, metrics]) => metrics);
                                                        if (usedN.length === 0) return null;
                                                        const title = row.notification_name || `Notification ${nid}`;
                                                        const path = row.notification_dashboard_path;
                                                        const recipKey = `__all__::${nid}`;
                                                        const recipExpanded = !!expandedNotifRecipKeys[recipKey];
                                                        const recipSlot = notifRecipDetailByKey[recipKey];
                                                        return (
                                                            <div key={`totals-nf-${nid}`} className={classes.tourNotifBlock}>
                                                                <div className={classes.tourNotifTitleRow} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                                                    {path ? (
                                                                        <Link
                                                                            href={path}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className={classes.tourCardTitle}
                                                                            title={title}
                                                                        >
                                                                            {title}
                                                                            <OpenInNewIcon
                                                                                style={{ fontSize: 14, marginLeft: 6, verticalAlign: 'middle', opacity: 0.55 }}
                                                                            />
                                                                        </Link>
                                                                    ) : (
                                                                        <Typography component="span" color="text.secondary" fontStyle="italic">
                                                                            {title}
                                                                        </Typography>
                                                                    )}
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => handleToggleNotifRecipients('__all__', nid)}
                                                                        sx={{ ml: 'auto', minWidth: 0, fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                                                                        aria-expanded={recipExpanded}
                                                                        endIcon={(
                                                                            <ExpandMoreIcon
                                                                                className={classes.tourNotifExpandIcon}
                                                                                sx={{ fontSize: 16 }}
                                                                                style={{ transform: recipExpanded ? 'rotate(180deg)' : 'none' }}
                                                                            />
                                                                        )}
                                                                    >
                                                                        Recipients
                                                                    </Button>
                                                                </div>
                                                                {usedN.map(([m, metrics]) => (
                                                                    <MediumRowWithSplit key={m} medium={m} metrics={metrics} />
                                                                ))}
                                                                <Collapse in={recipExpanded} timeout="auto" unmountOnExit>
                                                                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #E0E0E0' }}>
                                                                        <NotificationRecipientsPanel slot={recipSlot} />
                                                                    </Box>
                                                                </Collapse>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </Collapse>
                                    </Card>
                                );
                            })()}
                        </div>
                    </>
                )}
            </div>
        </Drawer>
    );
}


const NotificationMetricsList = ({
    forceSource,
    chChatTotals,
    showDebugInfo = false,
    analyticsTabSlug = 'notification_metrics',
} = {}) => {
    const restrictLiveDateRange = Boolean(!showDebugInfo && forceSource === 'ch');
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'report/notification_metrics';
    const appDownloadsResource = 'report/notification_metrics/app_downloads';
    const textReportResource = 'report/notification_metrics/text_report';
    const classes = useStyles();
    const [syncActive, setSyncActive] = React.useState(false);
    const [exportError, setExportError] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm');
    const { roleMap } = React.useContext(ContextStore);
    const [query, setQuery] = React.useState(null);
    const location = useLocation();
    const dataGridApiRef = useGridApiRef();

    // Read-rates rows fetched directly via dataAPI so we can render them as
    // event-grouped cards instead of a flat grid. The medium filter on the
    // toolbar is still honored (sent to the backend); event grouping is
    // purely a frontend concern.
    const [readRates, setReadRates] = React.useState([]);
    const [readRatesLoading, setReadRatesLoading] = React.useState(false);
    const [readRatesError, setReadRatesError] = React.useState(null);
    const [responseMeta, setResponseMeta] = React.useState(null);
    // CP-8080: consolidated distinct-guest "read via any method" counts,
    // keyed by event_code (plus an ``overall`` union across events). Returned
    // as a sibling of ``results`` by the report endpoint; may be null when
    // the backend computation failed (cards then simply omit the row).
    const [uniqueReached, setUniqueReached] = React.useState(null);
    // True while the dedicated unique-reached request is in flight. Drives
    // the skeleton "Any method" rows in the event cards so the row has a
    // visible placeholder instead of popping in when the slow distinct-scan
    // response arrives.
    const [uniqueReachedLoading, setUniqueReachedLoading] = React.useState(false);

    // Main-grid refresh: bumping ``gridReloadToken`` refetches read rates +
    // unique-reached with ``refresh=1`` (bypasses the server's 5-min cache).
    // ``gridRollingUp`` is true while the pre-refetch rollup_today POST runs,
    // mirroring the tour-breakdown drawer's two-step refresh.
    const [gridReloadToken, setGridReloadToken] = React.useState(0);
    const [gridRollingUp, setGridRollingUp] = React.useState(false);

    const [appDownloads, setAppDownloads] = React.useState(null);
    const [appDownloadsLoading, setAppDownloadsLoading] = React.useState(false);
    const [appDownloadsError, setAppDownloadsError] = React.useState(null);
    // App Downloads cohort: 'all' | 'with' | 'without' contact details
    // (backend ``contact_details`` filter, ``User.has_contact_info``).
    const [contactDetails, setContactDetails] = React.useState('all');

    // Tour Breakdown drawer (per-event drill-down). Null = closed; setting
    // to an event_code opens the drawer and triggers the on-demand fetch.
    const [breakdownEventCode, setBreakdownEventCode] = React.useState(null);
    const handleShowTourBreakdown = React.useCallback((eventCode) => {
        setBreakdownEventCode(eventCode);
    }, []);
    const handleCloseTourBreakdown = React.useCallback(() => {
        setBreakdownEventCode(null);
    }, []);
    // Pure client-side text filter for the App Installs by Tour grid. The
    // by_tour payload arrives in full (~100–150 rows), so filtering in
    // React is instant and avoids any new round-trip.
    const [tourSearch, setTourSearch] = React.useState('');

    const [recordListFilters, setRecordListFilters] = React.useState([
        { field: 'start_datetime', value: null },
        { field: 'end_datetime', value: null },
        { field: 'mediums', value: null },
        { field: 'event_codes', value: null },
    ]);

    React.useEffect(() => {
        const params = new URLSearchParams();
        const filters = [...recordListFilters];
        filters.forEach((filter, index) => {
            if (filter.value) {
                params.append(index, JSON.stringify(filter));
            }
        });
        if (query) params.append('query', query);
        params.set('tab', analyticsTabSlug);
        history.push({ pathname: location.pathname, search: params.toString() });
    }, [recordListFilters, query, analyticsTabSlug]);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const filters = [];
        let hasUrlDateFilter = false;
        for (const [key, value] of params.entries()) {
            if (key === 'query') { setQuery(value); continue; }
            if (key === 'tab') continue;
            try {
                const f = JSON.parse(value);
                if (f && (f.field === 'start_datetime' || f.field === 'end_datetime') && f.value) {
                    hasUrlDateFilter = true;
                }
                filters.push(f);
            } catch { /* skip malformed */ }
        }
        const fields = ['start_datetime', 'end_datetime', 'mediums', 'event_codes'];
        fields.forEach((field) => {
            if (!filters.some((f) => f.field === field)) {
                filters.push({ field, value: null });
            }
        });
        // Default to "Last 7 days" on initial load when the URL doesn't pin
        // an explicit date range. Operators almost always want recent
        // activity first, and short ranges keep the live-computed App
        // Downloads section (~1.5s for 7d vs ~10s+ for 365d) responsive.
        if (!hasUrlDateFilter) {
            const today = moment.tz(metricsTimezone).format('YYYY-MM-DD');
            const sevenDaysAgo = moment.tz(metricsTimezone).subtract(7, 'days').format('YYYY-MM-DD');
            const startIdx0 = filters.findIndex((f) => f.field === 'start_datetime');
            const endIdx0 = filters.findIndex((f) => f.field === 'end_datetime');
            if (startIdx0 >= 0) filters[startIdx0] = { ...filters[startIdx0], value: sevenDaysAgo };
            if (endIdx0 >= 0) filters[endIdx0] = { ...filters[endIdx0], value: today };
        }

        const startIdx = filters.findIndex((f) => f.field === 'start_datetime');
        const endIdx = filters.findIndex((f) => f.field === 'end_datetime');
        if (restrictLiveDateRange && startIdx >= 0 && endIdx >= 0) {
            const tz0 = metricsTimezone;
            const stVal = filters[startIdx].value;
            const enVal = filters[endIdx].value;
            if (stVal == null || enVal == null) {
                filters[startIdx] = { ...filters[startIdx], value: moment.tz(tz0).subtract(7, 'days').format('YYYY-MM-DD') };
                filters[endIdx] = { ...filters[endIdx], value: moment.tz(tz0).format('YYYY-MM-DD') };
            } else {
                const gap = moment.tz(enVal, 'YYYY-MM-DD', tz0).diff(
                    moment.tz(stVal, 'YYYY-MM-DD', tz0), 'days',
                );
                if (gap > LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS) {
                    filters[endIdx] = {
                        ...filters[endIdx],
                        value: moment.tz(stVal, 'YYYY-MM-DD', tz0)
                            .add(LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS, 'days')
                            .format('YYYY-MM-DD'),
                    };
                }
            }
        }

        setRecordListFilters(filters);
    }, []);

    const [metricsTimezone, setMetricsTimezone] = React.useState(() =>
        readStoredMetricsTimezoneOrLocalDefault(STORAGE_KEY_NOTIFICATION_METRICS_TZ)
    );
    const handleMetricsTimezoneChange = React.useCallback((tz) => {
        if (!tz || !moment.tz.zone(tz)) return;
        try { window.localStorage.setItem(STORAGE_KEY_NOTIFICATION_METRICS_TZ, tz); }
        catch { /* ignore */ }
        setMetricsTimezone(tz);
    }, []);

    const filtersForReadRates = React.useMemo(
        () => [...recordListFilters, { field: 'timezone', value: metricsTimezone }],
        [recordListFilters, metricsTimezone]
    );
    const startVal = recordListFilters.find((f) => f.field === 'start_datetime')?.value || null;
    const endVal = recordListFilters.find((f) => f.field === 'end_datetime')?.value || null;
    const mediumsVal = recordListFilters.find((f) => f.field === 'mediums')?.value || null;
    // "All available" uses both null; a bounded custom range needs both edges.
    // A lone start (e.g. after changing start in the date picker) must not
    // trigger report requests until the user finishes the range.
    const metricsDatesReady =
        (startVal == null && endVal == null) ||
        (startVal != null && endVal != null);

    // Cap the per-event tour breakdown drawer to a bounded window.
    // PG (MessageLog live scan) is slow on wide ranges — 30-day windows
    // on busy operators measured >2 min. CH handles the same queries in
    // seconds, so we allow a much wider window there.
    const TOUR_BREAKDOWN_MAX_DAYS = forceSource === 'ch' ? 90 : 7;
    const tourBreakdownRangeDays = React.useMemo(() => {
        if (!startVal || !endVal) return null;
        const start = new Date(startVal);
        const end = new Date(endVal);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
        // Gap-based count (no +1) to match the date-picker preset
        // semantics: "Last 7" selects e.g. 2026-05-01 → 2026-05-08, and
        // users read that as 7 days, not 8. Using the inclusive count
        // would falsely flag the built-in "Last 7" preset as
        // out-of-range.
        return Math.round((end - start) / (1000 * 60 * 60 * 24));
    }, [startVal, endVal]);
    const tourBreakdownDisabled = (
        tourBreakdownRangeDays != null && tourBreakdownRangeDays > TOUR_BREAKDOWN_MAX_DAYS
    );

    // True only for the fetch cycle triggered by the refresh button, so a
    // later filter change doesn't keep bypassing the server cache.
    const gridRefreshPendingRef = React.useRef(false);

    const handleGridRefresh = React.useCallback(async () => {
        // Two-step refresh, mirroring the tour-breakdown drawer: advance
        // today's rollup checkpoint first so just-sent messages become
        // visible, then refetch with refresh=1 to bypass the server caches.
        // rollup_today soft-fails; the refetch proceeds regardless.
        setGridRollingUp(true);
        try {
            await dataAPI(CREATE, 'report/notification_metrics/rollup_today', {
                data: { date_from: startVal, date_to: endVal, timezone: metricsTimezone },
            });
        } catch (_err) {
            // Soft-fail: still proceed with the cache-bypass refetch.
        } finally {
            setGridRollingUp(false);
            gridRefreshPendingRef.current = true;
            setGridReloadToken((n) => n + 1);
        }
    }, [dataAPI, startVal, endVal, metricsTimezone]);

    // Fetch read rates whenever filters change.
    React.useEffect(() => {
        const ac = new AbortController();
        let active = true;
        if (!metricsDatesReady) {
            setReadRatesLoading(false);
            setReadRatesError(null);
            setReadRates([]);
            setResponseMeta(null);
            setUniqueReachedLoading(false);
            return () => { active = false; ac.abort(); };
        }
        const isRefresh = gridRefreshPendingRef.current;
        gridRefreshPendingRef.current = false;
        (async () => {
            setReadRatesLoading(true);
            setReadRatesError(null);
            // Clear stale cards so changing the date/timezone doesn't keep
            // showing the previous range until the HTTP response returns.
            setReadRates([]);
            setUniqueReached(null);
            try {
                const readRatesFilter = { filters: JSON.stringify(filtersForReadRates) };
                if (forceSource) readRatesFilter.source = forceSource;
                if (chChatTotals) readRatesFilter.ch_chat_totals = chChatTotals;
                if (isRefresh) readRatesFilter.refresh = '1';
                const res = await dataAPI(GET_LIST, resource, {
                    pagination: { page: 1, perPage: 500 },
                    sort: { field: 'event_code', order: 'ASC' },
                    filter: readRatesFilter,
                    convert: (raw) => {
                        if (raw && typeof raw === 'object' && 'meta' in raw) {
                            setResponseMeta(raw.meta || null);
                        }
                        // Older backends inline unique_reached here; newer
                        // ones return null and serve it from the dedicated
                        // endpoint below — only accept a real value so the
                        // inline null can't clobber the parallel fetch.
                        if (raw && typeof raw === 'object' && raw.unique_reached) {
                            setUniqueReached(raw.unique_reached);
                        }
                        return raw;
                    },
                    signal: ac.signal,
                });
                if (!active) return;
                if (res?.statusCode >= 400) {
                    setReadRatesError(`Failed to load (HTTP ${res.statusCode})`);
                    setReadRates([]);
                } else {
                    setReadRates(res?.data || []);
                }
            } catch (err) {
                if (active && err?.name !== 'AbortError') {
                    setReadRatesError(err?.message || 'Failed to load read rates');
                    setReadRates([]);
                }
            } finally {
                if (active) setReadRatesLoading(false);
            }
        })();
        // Unique-reached ("read via any method") loads in parallel so its
        // MessageLog distinct scan (slow on wide ranges) never blocks the
        // grid's first paint. Non-fatal: on failure (or against an older
        // backend without the endpoint) the row simply stays hidden, and
        // older backends still deliver it inline via the convert above.
        (async () => {
            setUniqueReachedLoading(true);
            try {
                const uniqueFilter = { filters: JSON.stringify(filtersForReadRates) };
                if (forceSource) uniqueFilter.source = forceSource;
                if (isRefresh) uniqueFilter.refresh = '1';
                const res = await dataAPI(GET_ONE, 'report/notification_metrics/unique_reached', {
                    filter: uniqueFilter,
                    signal: ac.signal,
                });
                if (!active) return;
                if (!(res?.statusCode >= 400) && res?.data?.data) {
                    setUniqueReached(res.data.data);
                }
            } catch (err) {
                /* non-fatal — see comment above */
            } finally {
                if (active) setUniqueReachedLoading(false);
            }
        })();
        return () => { active = false; ac.abort(); };
    }, [metricsDatesReady, startVal, endVal, mediumsVal, metricsTimezone, dataAPI, resource, forceSource, chChatTotals, gridReloadToken]);

    // Fetch app downloads whenever date/timezone/cohort changes (mediums
    // filter doesn't affect this endpoint).
    React.useEffect(() => {
        const ac = new AbortController();
        const filtersForDownloads = [
            { field: 'start_datetime', value: startVal },
            { field: 'end_datetime', value: endVal },
            { field: 'timezone', value: metricsTimezone },
            { field: 'contact_details', value: contactDetails },
        ];
        let active = true;
        if (!metricsDatesReady) {
            setAppDownloadsLoading(false);
            setAppDownloadsError(null);
            setAppDownloads(null);
            return () => { active = false; ac.abort(); };
        }
        (async () => {
            setAppDownloadsLoading(true);
            setAppDownloadsError(null);
            // Clear stale data so the loading indicator (not the previous
            // range's stats) is what the user sees while the new range
            // computes. App Downloads is live-computed and noticeably
            // slower than Read Rates, so showing stale data here while
            // the spinner is on the toolbar would be misleading.
            setAppDownloads(null);
            try {
                const res = await dataAPI(GET_ONE, appDownloadsResource, {
                    filter: { filters: JSON.stringify(filtersForDownloads) },
                    signal: ac.signal,
                });
                if (!active) return;
                if (res?.statusCode >= 400) {
                    setAppDownloadsError(`Failed to load (HTTP ${res.statusCode})`);
                    setAppDownloads(null);
                } else {
                    setAppDownloads(res?.data || null);
                }
            } catch (err) {
                if (active && err?.name !== 'AbortError') {
                    setAppDownloadsError(err?.message || 'Failed to load App Downloads');
                    setAppDownloads(null);
                }
            } finally {
                if (active) setAppDownloadsLoading(false);
            }
        })();
        return () => { active = false; ac.abort(); };
    }, [metricsDatesReady, startVal, endVal, metricsTimezone, contactDetails, dataAPI, appDownloadsResource]);

    // ---------------------------------------------------------------- Section 1
    const eventGroups = React.useMemo(() => {
        const byEvent = new Map();
        for (const row of readRates) {
            if (!byEvent.has(row.event_code)) byEvent.set(row.event_code, {});
            byEvent.get(row.event_code)[row.medium] = row;
        }
        const eventCodes = [...byEvent.keys()].sort(compareNotificationMetricsEventCodes);
        return eventCodes.map((eventCode) => {
            const mediumMap = byEvent.get(eventCode);
            const mediumRows = MEDIUM_DISPLAY_ORDER
                .filter((m) => mediumMap[m] && (mediumMap[m].total > 0))
                .map((medium) => ({ medium, metrics: mediumMap[medium] }));
            return { eventCode, mediumRows };
        }).filter((g) => g.mediumRows.length > 0);
    }, [readRates]);

    // ---------- Balanced column count for the event-cards grid ----------
    // Native CSS Grid `auto-fill` packs as many columns as fit at the 340px
    // minimum, which produces lopsided last rows (e.g. 10 events → 6 + 4).
    // We measure the container's width and pick the smallest column count
    // that still uses the same row count `auto-fill` would have used,
    // distributing items evenly across rows (10 events → 5 + 5).
    const eventCardsGridRef = React.useRef(null);
    const [eventCardsGridWidth, setEventCardsGridWidth] = React.useState(0);
    React.useEffect(() => {
        const el = eventCardsGridRef.current;
        if (!el) return undefined;
        // Seed with a synchronous read so the first paint already uses the
        // balanced layout instead of briefly flashing the auto-fill default.
        setEventCardsGridWidth(Math.floor(el.getBoundingClientRect().width));
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setEventCardsGridWidth(Math.floor(entry.contentRect.width));
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [eventGroups.length]);
    const balancedEventCols = React.useMemo(() => {
        const n = eventGroups.length;
        if (!eventCardsGridWidth || n <= 1) return null;
        const cardMin = 340; // matches eventCardsGrid CSS minmax min
        const gap = 16;      // theme.spacing(2)
        const maxCols = Math.max(
            1,
            Math.floor((eventCardsGridWidth + gap) / (cardMin + gap))
        );
        if (maxCols >= n) return n;
        const rows = Math.ceil(n / maxCols);
        return Math.min(maxCols, Math.ceil(n / rows));
    }, [eventGroups.length, eventCardsGridWidth]);

    // ---------------------------------------------------------------- Section 3
    const stopOffByEvent = React.useMemo(() => {
        const byEvent = new Map();
        let total = 0;
        for (const row of readRates) {
            const c = row.stop_off_count || 0;
            if (c <= 0) continue;
            if (!byEvent.has(row.event_code)) byEvent.set(row.event_code, { total: 0, mediums: {} });
            const entry = byEvent.get(row.event_code);
            entry.mediums[row.medium] = (entry.mediums[row.medium] || 0) + c;
            entry.total += c;
            total += c;
        }
        const events = [...byEvent.entries()]
            .map(([eventCode, e]) => ({
                eventCode,
                total: e.total,
                mediums: MEDIUM_DISPLAY_ORDER
                    .filter((m) => e.mediums[m])
                    .map((m) => ({ medium: m, count: e.mediums[m] })),
            }))
            .sort((a, b) => compareNotificationMetricsEventCodes(a.eventCode, b.eventCode));
        return { events, total };
    }, [readRates]);

    // Both endpoints return ``earliest_available_date``; prefer whichever
    // arrived first. They should agree on most calls (same operator, same
    // checkpoint table).
    const earliestAvailableDate = (
        responseMeta?.earliest_available_date
        || appDownloads?.meta?.earliest_available_date
        || null
    );

    React.useEffect(() => {
        if (!earliestAvailableDate) return;
        const floor = moment.tz(earliestAvailableDate, 'YYYY-MM-DD', metricsTimezone).startOf('day');
        if (!floor.isValid()) return;

        setRecordListFilters((prev) => {
            const bump = (v) => {
                if (v == null || v === '') return v;
                const m = moment.tz(v, 'YYYY-MM-DD', metricsTimezone).startOf('day');
                if (!m.isValid()) return v;
                return m.isBefore(floor) ? floor.format('YYYY-MM-DD') : v;
            };

            const fStart = prev.find((f) => f.field === 'start_datetime');
            const fEnd = prev.find((f) => f.field === 'end_datetime');
            if (!fStart || !fEnd) return prev;

            let sv = bump(fStart.value);
            let ev = bump(fEnd.value);
            if (sv != null && ev != null
                && moment.tz(ev, 'YYYY-MM-DD', metricsTimezone).isBefore(moment.tz(sv, 'YYYY-MM-DD', metricsTimezone))
            ) {
                ev = sv;
            }
            if (
                restrictLiveDateRange &&
                sv != null &&
                ev != null &&
                moment.tz(ev, 'YYYY-MM-DD', metricsTimezone).diff(moment.tz(sv, 'YYYY-MM-DD', metricsTimezone), 'days')
                    > LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS
            ) {
                ev = moment.tz(sv, 'YYYY-MM-DD', metricsTimezone)
                    .add(LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS, 'days')
                    .format('YYYY-MM-DD');
            }

            if (sv === fStart.value && ev === fEnd.value) return prev;

            return prev.map((f) => {
                if (f.field === 'start_datetime') return { ...f, value: sv };
                if (f.field === 'end_datetime') return { ...f, value: ev };
                return f;
            });
        });
    }, [earliestAvailableDate, metricsTimezone, restrictLiveDateRange]);

    // ---------------------------------------------------------------- Renderers
    const renderCappedTodayNotice = () => {
        // Today is refreshed by a cron every ~30 minutes. This banner appears
        // only when the requested range extends to today but today's bucket
        // hasn't been rolled yet — typically a brief window after a deploy or
        // for a brand-new operator.
        if (!responseMeta?.capped_today) return null;
        return (
            <Alert severity="info" className={classes.notice}>
                Showing metrics through <strong>{responseMeta.date_to} UTC</strong>. Today's
                snapshot will appear within ~30 minutes.
            </Alert>
        );
    };

    // 6 cards in App Downloads. Cascade: 2 per row on phones (xs),
    // 3 per row on tablets/small desktops (sm/md), all 6 in a single row
    // on larger desktops (lg ≥ 1200px) so the section reads as one strip
    // when there's room for it. We hold off from collapsing to one row at
    // md (≥900px) because the drawer eats ~220px of width — at md we'd
    // squeeze each card to ~108px, which is too narrow for labels like
    // "Have App Installed" with the help-icon to render cleanly.
    const renderKpi = (label, value, subvalue, helpText) => (
        <Grid item xs={6} sm={4} lg={2}>
            <Card className={classes.kpiCard} variant="outlined">
                <CardContent className={classes.kpiCardContent}>
                    <Typography className={classes.kpiLabel}>
                        {label}
                        {helpText && (
                            <Tooltip title={helpText} arrow placement="top">
                                <IconButton size="small" style={{ padding: 0, color: '#999' }} onClick={(e) => e.stopPropagation()}>
                                    <HelpOutlineIcon style={{ fontSize: '13px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Typography>
                    <Typography className={classes.kpiValue}>{value}</Typography>
                    {subvalue && <Typography className={classes.kpiSubvalue}>{subvalue}</Typography>}
                </CardContent>
            </Card>
        </Grid>
    );

    const renderAppDownloadsSection = () => {
        // ``appDownloadsLoading`` is checked before the cached data so that
        // a filter change immediately swaps the stale numbers for a fresh
        // loading bar — the prior range's stats lingering on screen during
        // a 3–10s recompute looks like a bug. The fetch effect also sets
        // ``appDownloads`` back to ``null`` when a request starts, which
        // makes this branch the *only* path during a filter change.
        if (appDownloadsLoading) {
            return <Box className={classes.kpiContainer}><LinearProgress /></Box>;
        }
        if (appDownloadsError) {
            return (
                <Alert severity="error" className={classes.notice}>
                    Could not load App Downloads section: {appDownloadsError}
                </Alert>
            );
        }
        if (!appDownloads) return null;

        const s = appDownloads.summary || {};
        const days = appDownloads.days_before_tour || {};
        const totals = appDownloads.by_tour_totals || {};
        const uniqueUsers = s.unique_users || 0;
        const withApp = s.unique_with_app || 0;
        const installedInPeriod = s.installed_in_period || 0;
        const installedInPeriodTracked = s.installed_in_period_tracked || 0;
        const appPct = s.app_pct || 0;
        // Party-level rollups: lead booker + their +1 sub-bookings
        // collapsed to one unit. A party counts as "installed" if any
        // member installed by tour end. Useful for "did this booking
        // engage with the app?" since a 4-person family with one
        // installer is one successful booking, not 25%.
        const uniqueParties = s.unique_parties || 0;
        const partiesWithApp = s.unique_parties_with_app || 0;
        const partiesAppPct = s.parties_app_pct || 0;

        return (
            <>
                <Grid container spacing={2} className={classes.kpiContainer}>
                    {renderKpi(
                        'Messaged Users',
                        num(uniqueUsers),
                        `${num(s.total_users || 0)} trip-user records`,
                        'Distinct guest users who received at least one notification in the date range.'
                    )}
                    {renderKpi(
                        'Have App Installed',
                        num(withApp),
                        `${appPct.toFixed(2)}% of messaged users`,
                        'Distinct messaged users with the mobile app installed (point-in-time, evaluated now).'
                    )}
                    {renderKpi(
                        'Installed In Period',
                        num(installedInPeriod),
                        installedInPeriod !== installedInPeriodTracked
                            ? `${num(installedInPeriodTracked)} with first-seen timestamp`
                            : null,
                        'Messaged users whose app first-seen (or account creation, if first-seen is unknown) lands within the selected date range.'
                    )}
                    {renderKpi(
                        'App Install Rate',
                        `${appPct.toFixed(2)}%`,
                        uniqueUsers > 0 ? `${num(withApp)} of ${num(uniqueUsers)}` : null,
                        'Have App Installed ÷ Messaged Users.'
                    )}
                    {renderKpi(
                        'Parties Reached',
                        num(uniqueParties),
                        uniqueParties > 0 && uniqueUsers > 0
                            ? `${num(uniqueUsers)} individual travelers`
                            : null,
                        'Distinct booking parties (lead booker + their +1 / party-member sub-bookings collapsed to one unit) whose tours ended in this period.'
                    )}
                    {renderKpi(
                        'Party Install Rate',
                        `${partiesAppPct.toFixed(2)}%`,
                        uniqueParties > 0 ? `${num(partiesWithApp)} of ${num(uniqueParties)} parties` : null,
                        'Share of booking parties where at least one member installed the app by tour end. Often more meaningful than the per-individual rate when families share one device.'
                    )}
                </Grid>

                {/* Both cohorts side by side, whatever the Customers filter:
                    travelers without an email or phone never get the
                    download link, so the rate for reachable customers is the
                    one messaging can move. */}
                {(() => {
                    const split = appDownloads.contact_split || {};
                    const withC = split.with;
                    const withoutC = split.without;
                    if (!withC || !withoutC) return null;
                    const travelers = (withC.unique_users || 0) + (withoutC.unique_users || 0);
                    if (travelers === 0) return null;
                    const missingShare = (100 * (withoutC.unique_users || 0)) / travelers;
                    const stat = (label, value, sub, help) => (
                        <Box className={classes.daysStripStat}>
                            <Tooltip title={help} arrow placement="top">
                                <span className={classes.daysStripStatLabel}>{label}</span>
                            </Tooltip>
                            <span className={classes.daysStripStatValue}>{value}</span>
                            <span style={{ fontSize: '0.75rem', color: '#888' }}>{sub}</span>
                        </Box>
                    );
                    return (
                        <Box className={classes.daysStrip}>
                            <Typography style={{ alignSelf: 'center', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginRight: 8 }}>
                                Contact Details
                            </Typography>
                            {stat(
                                'With: Install Rate',
                                `${(withC.app_pct || 0).toFixed(2)}%`,
                                `${num(withC.unique_with_app || 0)} of ${num(withC.unique_users || 0)}`,
                                'Travelers with a real email address or a valid phone number who installed the app by tour end.'
                            )}
                            {stat(
                                'With: Party Rate',
                                `${(withC.parties_app_pct || 0).toFixed(2)}%`,
                                `${num(withC.unique_parties_with_app || 0)} of ${num(withC.unique_parties || 0)} parties`,
                                'Booking parties where at least one member has contact details, and at least one member installed the app by tour end.'
                            )}
                            {stat(
                                'Without: Install Rate',
                                `${(withoutC.app_pct || 0).toFixed(2)}%`,
                                `${num(withoutC.unique_with_app || 0)} of ${num(withoutC.unique_users || 0)}`,
                                'Travelers with no usable email or phone (missing, marked fake, or a shared OTA relay inbox). They never receive the download link.'
                            )}
                            {stat(
                                'Missing Contact Details',
                                `${missingShare.toFixed(1)}%`,
                                `${num(withoutC.unique_users || 0)} of ${num(travelers)} travelers`,
                                'Share of travelers in this date range with no usable email or phone.'
                            )}
                        </Box>
                    );
                })()}

                {days.count > 0 && (
                    <Box className={classes.daysStrip}>
                        <Typography style={{ alignSelf: 'center', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginRight: 8 }}>
                            Days Before Tour
                        </Typography>
                        <Box className={classes.daysStripStat}>
                            <span className={classes.daysStripStatLabel}>Median</span>
                            <span className={classes.daysStripStatValue}>{Number(days.median).toFixed(1)}</span>
                        </Box>
                        <Box className={classes.daysStripStat}>
                            <span className={classes.daysStripStatLabel}>Average</span>
                            <span className={classes.daysStripStatValue}>{Number(days.average).toFixed(1)}</span>
                        </Box>
                        <Box className={classes.daysStripStat}>
                            <span className={classes.daysStripStatLabel}>Mode</span>
                            <span className={classes.daysStripStatValue}>{days.mode}</span>
                        </Box>
                        <Box className={classes.daysStripStat}>
                            <span className={classes.daysStripStatLabel}>Min</span>
                            <span className={classes.daysStripStatValue}>{days.min}</span>
                        </Box>
                        <Box className={classes.daysStripStat}>
                            <span className={classes.daysStripStatLabel}>Max</span>
                            <span className={classes.daysStripStatValue}>{days.max}</span>
                        </Box>
                    </Box>
                )}

                {days.count === 0 && withApp > 0 && (
                    <Box
                        className={classes.daysStrip}
                        sx={{ justifyContent: 'flex-start' }}
                    >
                        <Typography style={{ alignSelf: 'center', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginRight: 8 }}>
                            Days Before Tour
                        </Typography>
                        <Typography style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#888', fontStyle: 'italic' }}>
                            No install-date data available for this period.
                        </Typography>
                    </Box>
                )}

                {/* Tours running right now. The "with app" count is
                    point-in-time ("has the app now"), unlike the settled
                    table's "installed by tour end" gate — a live tour's
                    count here can exceed its eventual settled number (a
                    traveler who installs after their personal end counts
                    now, not later). Deliberate product decision. Traveler
                    counts include only guests whose personal participation
                    is still active (early departures drop out mid-tour).
                    The copy is traveler-centric ("travelers... not counted
                    below") rather than tour-centric because an in-progress
                    tour CAN already appear in the table with a partial row:
                    travelers whose personal end_ts passed mid-tour (early
                    departures) settle individually while the tour runs.
                    The backend returns [] unless the range reaches today. */}
                {(appDownloads.in_progress || []).length > 0 && (() => {
                    const inProgress = appDownloads.in_progress;
                    const totalOnTour = inProgress.reduce((acc, t) => acc + (t.travelers_on_tour || 0), 0);
                    const totalWithApp = inProgress.reduce((acc, t) => acc + (t.travelers_with_app || 0), 0);
                    return (
                        <Alert severity="info" className={classes.notice}>
                            <span style={{ fontWeight: 600 }}>
                                {inProgress.length === 1
                                    ? '1 tour currently in progress'
                                    : `${inProgress.length} tours currently in progress`}
                                {` — ${num(totalWithApp)} of ${num(totalOnTour)} traveler${totalOnTour === 1 ? '' : 's'} currently on tour have the app`}
                            </span>
                            {' — these travelers are not counted in the table below until their tour participation finishes: '}
                            {inProgress.map((t, i) => (
                                <React.Fragment key={t.tour_id || i}>
                                    {i > 0 && ', '}
                                    <a
                                        href={t.tour_id ? `/trip/${t.tour_id}` : undefined}
                                        style={{ color: '#007AFF', textDecoration: 'none' }}
                                    >
                                        {t.tour_name || '(no tour name)'}
                                    </a>
                                    {` (${num(t.travelers_with_app)}/${num(t.travelers_on_tour)})`}
                                </React.Fragment>
                            ))}
                        </Alert>
                    );
                })()}

                {(appDownloads.by_tour || []).length > 0 && (() => {
                    const allTours = appDownloads.by_tour || [];
                    const q = tourSearch.trim().toLowerCase();
                    const filteredTours = q
                        ? allTours.filter((t) => (t.tour_name || '').toLowerCase().includes(q))
                        : allTours;
                    // When a search is active, recompute the footer totals
                    // from the filtered subset so the bottom row reflects
                    // what's visible. Without this, "TOTAL" would lie about
                    // the sum the user is actually looking at.
                    const visibleTotals = q
                        ? filteredTours.reduce(
                            (acc, t) => ({
                                total_travelers: acc.total_travelers + (t.total_travelers || 0),
                                app_installed: acc.app_installed + (t.app_installed || 0),
                                installed_in_period: acc.installed_in_period + (t.installed_in_period || 0),
                                total_parties: acc.total_parties + (t.total_parties || 0),
                                parties_with_app: acc.parties_with_app + (t.parties_with_app || 0),
                            }),
                            {
                                total_travelers: 0,
                                app_installed: 0,
                                installed_in_period: 0,
                                total_parties: 0,
                                parties_with_app: 0,
                            },
                        )
                        : totals;
                    // Pinned bottom totals row — rendered by the DataGrid
                    // itself (via ``totalRow`` → ``pinnedRows.bottom`` in
                    // RecordList) so the cells line up with the columns
                    // they summarize. The previous custom div used a
                    // simple ``3fr 1fr 1fr 1fr 1fr`` grid which didn't
                    // match the DataGrid's flex+minWidth math, so the
                    // numbers visually drifted from their columns.
                    const totalsRowData = {
                        id: 'totals',
                        tour_id: null,
                        tour_name: q
                            ? `MATCHING (${filteredTours.length} tours)`
                            : `TOTAL (${allTours.length} tours)`,
                        total_travelers: visibleTotals.total_travelers || 0,
                        app_installed: visibleTotals.app_installed || 0,
                        app_pct: visibleTotals.total_travelers > 0
                            ? (100 * (visibleTotals.app_installed || 0)) / visibleTotals.total_travelers
                            : null,
                        installed_in_period: visibleTotals.installed_in_period || 0,
                        total_parties: visibleTotals.total_parties || 0,
                        parties_with_app: visibleTotals.parties_with_app || 0,
                        party_pct: visibleTotals.total_parties > 0
                            ? (100 * (visibleTotals.parties_with_app || 0)) / visibleTotals.total_parties
                            : null,
                    };
                    return (
                    <>
                        <Box className={classes.sectionHeader}>
                            <Typography className={classes.sectionHeaderTitle}>App Installs by Tour</Typography>
                            <Typography className={classes.sectionHeaderSub}>
                                {q
                                    ? `${filteredTours.length} of ${allTours.length} tours`
                                    : `${allTours.length} tours`}
                            </Typography>
                        </Box>
                        {/* Search bar on its own row under the section
                            header. Keeps the title clean and gives the
                            input full breathing room on the left edge. */}
                        <Box sx={{ margin: (theme) => theme.spacing(0, 2, 1, 2) }}>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search tours..."
                                value={tourSearch}
                                onChange={(e) => setTourSearch(e.target.value)}
                                sx={{ width: 280 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 18, color: '#888' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: tourSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setTourSearch('')}
                                                aria-label="Clear tour search"
                                            >
                                                <ClearIcon style={{ fontSize: 16 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Box>
                        <RecordList
                            className={classes.byTourGrid}
                            // ``autoHeight`` sizes the grid to fit the
                            // visible row count exactly — no whitespace
                            // on short lists, no fixed cap on long ones.
                            // Realistic by_tour sizes are <200 rows, so
                            // losing virtualization here is fine; if we
                            // ever ship operators with thousands of
                            // tours we'd switch back to fixed height +
                            // virtualization.
                            autoHeight
                            totalRow={[totalsRowData]}
                            resource={appDownloadsResource}
                            disableSelectionOnClick
                            staticOptions={filteredTours.map((t) => ({
                                id: t.tour_id != null ? `tour-${t.tour_id}` : `tour-noid-${t.tour_name || ''}`,
                                ...t,
                            }))}
                            sortModel={[{ field: 'total_travelers', sort: 'desc' }]}
                            disableColumnFilter
                            isSmallScreen={isSmallScreen}
                            checkboxSelection={false}
                            disablePageUrlData
                            // Effectively unlimited — operators in practice
                            // have at most a few hundred tours in any given
                            // window, so a single "page" of 5000 covers all
                            // realistic data while keeping virtualization
                            // active inside the fixed-height container.
                            perPage={5000}
                            paginationMode="client"
                            sortingMode="client"
                            flavor="pro"
                            apiRef={dataGridApiRef}
                            autorefresh={false}
                            columns={[
                                {
                                    field: 'tour_name',
                                    headerName: 'Tour',
                                    sortable: true,
                                    flex: 3,
                                    minWidth: 280,
                                    renderCell: (p) => {
                                        // The pinned totals row shares the
                                        // ``tour_name`` column; render its
                                        // label as plain text (the row's
                                        // ``MuiDataGrid-row--totals`` class
                                        // already supplies bold styling).
                                        if (p.row.id === 'totals') {
                                            return <span>{p.row.tour_name}</span>;
                                        }
                                        return (
                                            <a
                                                href={p.row.tour_id ? `/trip/${p.row.tour_id}` : undefined}
                                                style={{ color: p.row.tour_id ? '#007AFF' : '#333', textDecoration: 'none' }}
                                                title={p.row.tour_name}
                                            >
                                                {p.row.tour_name || '(no tour name)'}
                                            </a>
                                        );
                                    },
                                },
                                {
                                    field: 'total_travelers',
                                    headerName: 'Travelers',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 110,
                                    renderCell: (p) => num(p.row.total_travelers),
                                },
                                {
                                    field: 'app_installed',
                                    headerName: 'Have App',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 110,
                                    renderCell: (p) => num(p.row.app_installed),
                                },
                                {
                                    field: 'app_pct',
                                    headerName: 'Install Rate',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 120,
                                    renderCell: (p) => (
                                        p.row.app_pct == null
                                            ? '—'
                                            : `${p.row.app_pct.toFixed(1)}%`
                                    ),
                                },
                                {
                                    field: 'total_parties',
                                    headerName: 'Parties',
                                    description: 'Distinct booking parties on this tour (lead booker + any +1 sub-bookings collapsed to one unit).',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 110,
                                    renderCell: (p) => num(p.row.total_parties || 0),
                                },
                                {
                                    field: 'parties_with_app',
                                    headerName: 'Parties with App',
                                    description: 'Booking parties where at least one member had the app installed by tour end.',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 140,
                                    renderCell: (p) => num(p.row.parties_with_app || 0),
                                },
                                {
                                    field: 'party_pct',
                                    headerName: 'Party Rate',
                                    description: 'Parties with App ÷ Parties. A 4-person family with one installer counts as 1 / 1 here, not 1 / 4.',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 110,
                                    renderCell: (p) => {
                                        if (p.row.party_pct == null) return '—';
                                        return `${p.row.party_pct.toFixed(1)}%`;
                                    },
                                },
                                {
                                    field: 'installed_in_period',
                                    headerName: 'Installed In Period',
                                    headerAlign: 'center',
                                    align: 'center',
                                    sortable: true,
                                    flex: 1,
                                    minWidth: 150,
                                    renderCell: (p) => num(p.row.installed_in_period),
                                },
                            ]}
                        />
                    </>
                    );
                })()}
            </>
        );
    };

    const renderStopOffSection = () => {
        if (!stopOffByEvent.events.length) {
            return (
                <Box className={classes.sectionPadding}>
                    <Typography style={{ color: '#888', fontStyle: 'italic', fontSize: '0.9rem' }}>
                        No STOP/OFF messages in this date range.
                    </Typography>
                </Box>
            );
        }
        return (
            <Box className={classes.sectionPadding}>
                <Typography style={{ marginBottom: 8, color: '#444' }}>
                    Total STOP/OFF messages: <strong>{num(stopOffByEvent.total)}</strong>
                </Typography>
                {stopOffByEvent.events.map(({ eventCode, total, mediums }) => (
                    <Paper key={eventCode} variant="outlined" className={classes.stopOffEvent}>
                        <Box className={classes.stopOffEventHeader}>
                            <span>
                                <span className={classes.eventCode}>{eventCode}</span>
                                <span style={{ marginLeft: 8, color: '#888', fontSize: '0.78rem' }}>
                                    {getEventLabel(eventCode)}
                                </span>
                            </span>
                            <Typography style={{ fontWeight: 600 }}>{num(total)}</Typography>
                        </Box>
                        <Box className={classes.stopOffMediums}>
                            {mediums.map(({ medium, count }) => (
                                <Chip
                                    key={medium}
                                    size="small"
                                    label={`${MEDIUM_LABEL[medium] || medium}: ${num(count)}`}
                                    style={{
                                        backgroundColor: MEDIUM_COLOR[medium] || '#999',
                                        color: 'white',
                                        fontSize: '0.72rem',
                                    }}
                                />
                            ))}
                        </Box>
                    </Paper>
                ))}
            </Box>
        );
    };

    // Plain-text report download. Posts the *current* date/medium/timezone
    // filters at the backend and saves the body as a .txt file using the
    // server-supplied Content-Disposition filename when available.
    const onExportList = React.useCallback(async () => {
        setExportError(null);
        if (!metricsDatesReady) {
            setExportError('Choose both start and end dates for the range before exporting.');
            return;
        }
        setSyncActive(true);
        try {
            const filtersForExport = [
                { field: 'start_datetime', value: startVal },
                { field: 'end_datetime', value: endVal },
                { field: 'timezone', value: metricsTimezone },
            ];
            const exportFilter = { filters: JSON.stringify(filtersForExport) };
            if (forceSource) exportFilter.source = forceSource;
            if (chChatTotals) exportFilter.ch_chat_totals = chChatTotals;
            const res = await dataAPI(GET_ONE, textReportResource, {
                filter: exportFilter,
            });
            const body = await res.response.blob();
            const url = window.URL.createObjectURL(body);

            let filename = '';
            // ``res.headers`` from ``httpClient`` is a fetch ``Headers`` instance,
            // so prefer ``.get(...)`` and fall back to bracket access for any
            // alternative wrappers in tests.
            const disposition = (typeof res.headers?.get === 'function'
                ? res.headers.get('Content-Disposition')
                : null)
                || res.headers?.['Content-Disposition']
                || res.headers?.['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (m && m[1]) filename = m[1].replace(/["']/g, '');
            }
            if (!filename) filename = 'notification_metrics_report.txt';

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            setTimeout(() => { document.body.removeChild(a); }, 100);
        } catch (err) {
            setExportError(err?.message || 'Failed to export report');
        } finally {
            setSyncActive(false);
        }
    }, [dataAPI, metricsDatesReady, startVal, endVal, metricsTimezone, textReportResource, forceSource, chChatTotals]);

    return (
        <div className={classes.root}>
            <PageMeta title="Analytics" />
            <FixedLinearProgress visibility={syncActive} />

            <Grid item xs={12} p={isSmallScreen ? 2 : 0} rb={0} style={{ marginBottom: !isSmallScreen ? 24 : 16 }}>
                <NotificationMetricsListToolbar
                    resource={resource}
                    query={query}
                    setRecordListFilters={setRecordListFilters}
                    setQuery={setQuery}
                    isSmallScreen={isSmallScreen}
                    roleMap={roleMap}
                    recordListFilters={recordListFilters}
                    metricsTimezone={metricsTimezone}
                    onMetricsTimezoneChange={handleMetricsTimezoneChange}
                    earliestAvailableDate={earliestAvailableDate}
                    restrictLiveDateRange={restrictLiveDateRange}
                    liveMaxRangeDaysGap={LIVE_NOTIFICATION_METRICS_MAX_RANGE_GAP_DAYS}
                    setLastUpdate={() => {}}
                    setSyncActive={setSyncActive}
                    onExportList={onExportList}
                />
            </Grid>

            {forceSource && showDebugInfo && (
                <Alert severity="info" className={classes.notice} icon={false} style={{ marginBottom: 8 }}>
                    Reading from <strong>{forceSource === 'ch' ? 'ClickHouse' : 'Postgres'}</strong>.
                    {forceSource === 'ch' && responseMeta?.metrics_ch_chat_totals && (
                        <span> In-app chat totals: <strong>{responseMeta.metrics_ch_chat_totals}</strong>.</span>
                    )}
                    {responseMeta?.metrics_aggregate_effective_engine && responseMeta.metrics_aggregate_effective_engine !== (forceSource === 'ch' ? 'clickhouse' : 'postgresql') && (
                        <span> (fell back to <strong>{responseMeta.metrics_aggregate_effective_engine}</strong>)</span>
                    )}
                    {responseMeta?.execution_time_ms != null && (
                        <span style={{ marginLeft: 8, opacity: 0.7 }}>
                            {responseMeta.execution_time_ms.toFixed(0)}ms
                        </span>
                    )}
                </Alert>
            )}
            {exportError && (
                <Alert severity="error" className={classes.notice} onClose={() => setExportError(null)}>
                    Could not export report: {exportError}
                </Alert>
            )}
            {renderCappedTodayNotice()}

            {/* Section 1 — Read Rates */}
            <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionHeaderTitle}>
                    1. Read Rates by Notification Event and Medium
                </Typography>
                <Typography className={classes.sectionHeaderSub}>
                    {gridRollingUp
                        ? 'Updating today’s metrics…'
                        : readRatesLoading ? 'Loading…' : `${eventGroups.length} events`}
                </Typography>
                <Tooltip title="Refresh: roll up today's metrics and reload, bypassing the cache" arrow>
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Refresh read rates"
                            onClick={handleGridRefresh}
                            disabled={gridRollingUp || readRatesLoading || !metricsDatesReady}
                            sx={{ alignSelf: 'center' }}
                        >
                            <ReloadIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {/* CP-8080: range-wide distinct-guest reach across all events and
                channels. Per-medium counts double-count guests messaged on
                more than one channel; this union is the number to compare
                against sales conversions. */}
            {/* Loading hint for the range-wide unique-reached summary below —
                mirrors the skeleton rows in the cards so the whole "any
                method" layer visibly announces itself while computing. */}
            {!readRatesLoading && uniqueReachedLoading && !uniqueReached && (
                <Typography
                    className={classes.sectionHeaderSub}
                    style={{ margin: '0 16px 8px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                    <CircularProgress size={11} thickness={5} style={{ color: '#999' }} />
                    Computing unique guests reached (any method) — distinct-guest
                    counts take a little longer than the per-channel rows…
                </Typography>
            )}
            {!readRatesLoading && uniqueReached?.overall?.unique_recipients > 0 && (
                <Typography
                    className={classes.sectionHeaderSub}
                    style={{ margin: '0 16px 8px 16px' }}
                >
                    Unique guests reached (any method):{' '}
                    <strong>{num(uniqueReached.overall.unique_read)}</strong> read of{' '}
                    <strong>{num(uniqueReached.overall.unique_recipients)}</strong> reached
                    {' '}({pct(uniqueReached.overall.unique_read_rate)}) — each guest counted
                    once across email, SMS, WhatsApp, in-app chat and Airbnb
                    {uniqueReached.overall.email_and_text_recipients > 0 && (
                        <>
                            {'; '}<strong>{num(uniqueReached.overall.email_and_text_recipients)}</strong>{' '}
                            received both email and SMS/WhatsApp
                        </>
                    )}
                    {uniqueReached.overall.sms_only_recipients > 0 && (
                        <>
                            {'; '}{num(uniqueReached.overall.sms_only_recipients)} reachable
                            only by SMS (no read tracking)
                        </>
                    )}
                    .
                </Typography>
            )}
            {readRatesLoading && (
                <Box className={classes.kpiContainer}>
                    <LinearProgress />
                </Box>
            )}
            {readRatesError && (
                <Alert severity="error" className={classes.notice}>
                    Could not load read rates: {readRatesError}
                </Alert>
            )}
            {eventGroups.length === 0 && !readRatesLoading && !readRatesError && (
                <Box className={classes.sectionPadding}>
                    <Typography style={{ color: '#888', fontStyle: 'italic', fontSize: '0.9rem' }}>
                        No notification data found in this date range.
                    </Typography>
                </Box>
            )}
            {eventGroups.length > 0 && (
                <Box
                    ref={eventCardsGridRef}
                    className={classes.eventCardsGrid}
                    style={
                        balancedEventCols
                            ? { gridTemplateColumns: `repeat(${balancedEventCols}, minmax(340px, 1fr))` }
                            : undefined
                    }
                >
                    {eventGroups.map(({ eventCode, mediumRows }) => (
                        <EventBlock
                            key={eventCode}
                            eventCode={eventCode}
                            mediumRows={mediumRows}
                            uniqueReached={uniqueReached?.events?.[eventCode] || null}
                            uniqueReachedLoading={uniqueReachedLoading}
                            onShowTourBreakdown={handleShowTourBreakdown}
                            tourBreakdownDisabled={tourBreakdownDisabled}
                            tourBreakdownDisabledReason={
                                tourBreakdownDisabled
                                    ? `Tour breakdown is limited to ${TOUR_BREAKDOWN_MAX_DAYS}-day windows (your range is ${tourBreakdownRangeDays} days). Narrow the date filter to enable this.`
                                    : null
                            }
                        />
                    ))}
                </Box>
            )}

            <Divider style={{ margin: '24px 16px 0 16px' }} />

            {/* Section 2 — App Downloads */}
            <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionHeaderTitle}>2. App Downloads</Typography>
            </Box>
            {/* Make the by-tour-end-date semantics explicit so the date
                filter doesn't read the same as it does for Read Rates
                (which keys off message-sent date, not tour-end date). */}
            <Typography
                className={classes.sectionHeaderSub}
                style={{ margin: '0 16px 8px 16px' }}
            >
                Travelers whose tours ended in the selected date range. A traveler
                counts as "installed" if their app download landed on or before
                the tour end date — in-flight tours are excluded.
            </Typography>
            <Box sx={{ margin: (theme) => theme.spacing(0, 2, 1, 2) }}>
                <TextField
                    select
                    size="small"
                    label="Customers"
                    value={contactDetails}
                    onChange={(e) => setContactDetails(e.target.value)}
                    sx={{ minWidth: 260 }}
                    helperText="Contact details = a real email address or a valid phone number"
                >
                    <MenuItem value="all">All customers</MenuItem>
                    <MenuItem value="with">With contact details</MenuItem>
                    <MenuItem value="without">Without contact details</MenuItem>
                </TextField>
            </Box>
            {renderAppDownloadsSection()}

            <Divider style={{ margin: '24px 16px 0 16px' }} />

            {/* Section 3 — STOP/OFF */}
            <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionHeaderTitle}>
                    3. STOP/OFF Messages by Notification Event and Medium
                </Typography>
            </Box>
            {renderStopOffSection()}

            <TourBreakdownDrawer
                open={breakdownEventCode != null}
                eventCode={breakdownEventCode}
                dateFrom={startVal}
                dateTo={endVal}
                timezone={metricsTimezone}
                mediumsFilter={mediumsVal}
                onClose={handleCloseTourBreakdown}
                tourBreakdownSource={forceSource === 'ch' ? 'ch' : 'pg'}
                chChatTotals={chChatTotals}
                showDebugInfo={showDebugInfo && Boolean(forceSource)}
            />
        </div>
    );
};

export default NotificationMetricsList;
